import { store, store$ } from '@redux/store';
import LoadingStatus from 'enums/loadingStatus';
import { selectCurrentUrl } from 'features/routing';
import { FavoriteType, selectPendingSaves } from 'features/userProfile';
import {
  ResultType,
  selectAppliedFacets,
  selectFetchCountsStatusAndsCounts,
  selectPage,
  selectIsNeighborhoodType,
  selectSorting,
  selectIsSchoolDistrictType
} from 'features/searchResultsFacets';
import { segmentIdentify, segmentImpression, segmentPageView, segmentTrack } from 'features/segment';
import {
  newListingSaved$,
  paidCommunityModalSubmission$,
  basicCommunityModalSubmission$,
  paidHomeModalSubmission$,
  basicPlanModalSubmission$,
  basicSpecModalSubmission$,
  basicListingModalSubmission$,
  paidManufacturedLocationModalSubmission$,
  paidManufacturedPlanModalSubmission$,
  paidManufacturedSpecModalSubmission$,
  recommendedCommunitiesSubmitted$,
  recommendedCommunitiesSubmittedBasic$,
  summaryCardLinkClicked$,
  summaryCardCtaClicked$,
  markerUrlOpened$,
  markerSummaryCardClicked$,
  pendingListingSavesFavorites$,
  removedFavorites$,
  carouselCloseButtonClicked$,
  communityStaticMapHovered$,
  communityStaticMapClicked$,
  communityStaticMapDoubleClicked$,
  communityStaticMapZoomChanged$,
  createdSaveSearchModalShown$,
  onSavedSearchClicked$,
  onKeepSearchingButtonClicked$,
  onDashboardLinkClicked$,
  summaryCardsCarouselScrolled$,
} from 'pages/searchResults/redux/observables';
import { 
  combineLatest,
  distinctUntilChanged, 
  distinctUntilKeyChanged, 
  filter, 
  fromEvent, 
  map, 
  pairwise, 
  skip,
  tap} from 'rxjs';
import ImpressionEventObserver from 'utils/impressionEventObserver';
import { selectHousingProductLocationFetchStatus, selectRequestStatus } from 'features/locationContent';
import { MarkerType } from 'features/searchResultsMap';
import { selectSelectedMarker } from 'pages/searchResults/redux/selectors';
import PageType from 'enums/pageType';


// #region List View Section
/**
 *  ! List View Section
 *  All events related elements located in the list view should be placed in this region
 */

/**
 * * This selector will work as an event delegator for all the click events
 * * that must be attached to elements inside the list view section
 */
const paidCommunityCardSelector = '[data-standard-card][data-paid-community]';
const paidBuildOnYourLotCommunityCardSelector = '[data-standard-card][data-paid-boyl-location]';
const paidCustomCommunityCardSelector = '[data-standard-card][data-paid-custom-location]';
const paidManufacturedCommunityCardSelector = '[data-standard-card][data-paid-manufactured-location]';
const basicCommunityCardSelector = '[data-standard-card][data-basic-community]';
const basicBuildOnYourLotCommunityCardSelector = '[data-standard-card][data-basic-boyl-location]';
const basicCustomCommunityCardSelector = '[data-standard-card][data-basic-custom-location]';
const basicManufacturedCommunityCardSelector = '[data-standard-card][data-basic-manufactured-location]';
const paidPlanCardSelector = '[data-standard-card][data-paid-plan]';
const paidSpecCardSelector = '[data-standard-card][data-paid-spec]';
const basicPlanCardSelector = '[data-standard-card][data-basic-plan]';
const basicSpecCardSelector = '[data-standard-card][data-basic-spec]';
const basicCustomPlanCardSelector = '[data-standard-card][data-basic-custom-plan]';
const basicCustomSpecCardSelector = '[data-standard-card][data-basic-custom-spec]';
const basicBuildOnYourLotPlanCardSelector = '[data-standard-card][data-basic-boyl-plan]';
const basicBuildOnYourLotSpecCardSelector = '[data-standard-card][data-basic-boyl-spec]';
const basicListingCardSelector = '[data-standard-card][data-basic-listing]'
const paidManufacturedPlanCardSelector = '[data-standard-card][data-paid-manufactured-plan]';
const paidManufacturedSpecCardSelector = '[data-standard-card][data-paid-manufactured-spec]';
const basicManufacturedPlanCardSelector = '[data-standard-card][data-basic-manufactured-plan]';
const basicManufacturedSpecCardSelector = '[data-standard-card][data-basic-manufactured-spec]';
const paidCustomPlanCardSelector = '[data-standard-card][data-paid-custom-plan]';
const paidCustomSpecCardSelector = '[data-standard-card][data-paid-custom-spec]';
const paidBuildOnYourLotPlanCardSelector = '[data-standard-card][data-paid-boyl-plan]';
const paidBuildOnYourLotSpecCardSelector = '[data-standard-card][data-paid-boyl-spec]';
const cardfavoriteButtonSelector = '[data-favorite-button]';
const listViewSection = document.querySelector('[data-list-view-element]');
const listViewSectionClicks$ = fromEvent(listViewSection, 'click');
const customAndBuildOnYourLotHomesSelectors = [
  basicCustomPlanCardSelector,
  basicCustomSpecCardSelector,
  basicBuildOnYourLotPlanCardSelector,
  basicBuildOnYourLotSpecCardSelector,
  paidCustomSpecCardSelector,
  paidBuildOnYourLotPlanCardSelector,
  paidBuildOnYourLotSpecCardSelector];
const customAndBuildOnYourLotCommunitiesSelectors = [
  paidBuildOnYourLotCommunityCardSelector,
  paidCustomCommunityCardSelector,
  basicBuildOnYourLotCommunityCardSelector,
  basicCustomCommunityCardSelector]; 

/**
 * Checks if a the page is showing the no result content.
 */
const isNotResultPage = () => !!listViewSection.querySelector('[data-no-result="true"]');

/**
 * Checks if it is a New Session.
 */
function isUserNewSession() {
  return !!sessionStorage.getItem('isNewSession'); 
};

/**
 * isNeighborhoodPage: Gets the selector for the neighborhood type to know if the page is neighborhood.
 * @returns A bool to determine if it is neighborhood or not.
 */
const isNeighborhoodPage = () => {
  const state = store.getState();
  return selectIsNeighborhoodType(state);
};

/**
 * isSchoolDistrictPage: Gets the selector for the school district type to know if the page is school district.
 * @returns A bool to determine if it is school district or not.
 */
const isSchoolDistrictPage = () => {
  const state = store.getState();
  return selectIsSchoolDistrictType(state);
};

/**
* Set New Session.
*/
function setUserNewSession() {
  sessionStorage.setItem('isNewSession', 'true');
}

const triggerIdentifyNewSession = () => {
  store.dispatch(segmentIdentify({ eventName: 'New Session' }));
};

/**
 * Checks if the acxiom data is saved.
 */
const acxiomDataSaved$ = fromEvent(document, 'acxiom-data-saved').pipe(
  filter(() => !isUserNewSession()),
  tap(setUserNewSession)
);

acxiomDataSaved$.subscribe(triggerIdentifyNewSession);

/* 
Object that maps the names of the areas clicked on a card element to the corresponding segment event property. 
*/
const segmentAreaClickedPerCardElement = {
  price: 'Price Area',
  priceReduction: 'Price Area',
  communityName: 'Community Name Area',
  planName: 'Plan Name Area',
  specAddress: 'Spec Address Area',
  image: 'Image Area',
  brandName: 'Builder Name Area',
  ratingsScore: 'Ratings Score Area',
  default: 'Card',
};

/**
 * The getCardImageUrls function takes a cardElement as an argument and returns an object with the following properties:
 * primaryImage, secondaryImage, terciaryImage.
 * if there are no images in the cardElement then each property will be assigned to an empty string.
 * This can be used to other events in the list view section.
 * @param cardElement Get the image elements from the card element
 *
 * @return An object with primaryImage, secondaryImage, terciaryImage.
 */
const getCardImageUrls = (cardElement) => {
  const imageElements = cardElement.querySelectorAll('img');

  if (imageElements) {
    return {
      primaryImage: imageElements[0]?.src || '',
      secondaryImage: imageElements[1]?.src || '',
      terciaryImage: imageElements[2]?.src || '',
    };
  }

  return {};
};

// ! Community Lead Button Click Event //

/**
 * The getLeadButtonClickedCommunityCustomProperties function is used to get the custom properties
 * for communities for the event LeadButtonClicked
 * @param cardDataset The card dataset
 * @param buttonDataset The button dataset
 * @param isPaid Bool that define if the community is paid or basic
 */
const getLeadButtonClickedCommunityCustomProperties = (cardDataset, buttonDataset, isPaid) => {
  const customProperties = {
    market_id: cardDataset.marketId,
    market_name: `${cardDataset.marketName}, ${cardDataset.stateAbbreviation}`,
    city: cardDataset.city,
    state: cardDataset.stateAbbreviation,
    zip: cardDataset.zip,
    builder_id: cardDataset.builderId,
    builder_name: cardDataset.brandName,
    community_id: cardDataset.id,
    community_name: cardDataset.communityName,
    community_status: cardDataset.marketingStatusLabel,
    primary_image_url: cardDataset.imageUrl,
    community_low_price: cardDataset.priceLow,
    community_high_price: cardDataset.priceHigh,
    button_action: buttonDataset.buttonAction,
    is_paid: isPaid,
    is_spotlight: !!cardDataset.wasMatchedAsSpotlightResult,
    community_type: cardDataset.communityType,
    is_neighborhood: isNeighborhoodPage(),
    is_schooldistrict: isSchoolDistrictPage(),
    position: +cardDataset.index + 1,
  };

  customProperties[
    cardDataset.type === "spec" ? "specName" : "planName"
  ] = cardDataset.name;

  return customProperties;
};

// observable created when there is a click in the list view.
// It will filter the actions to identify the ones performed in the CTA button.
const paidCommunityLeadButtonClicked$ = listViewSectionClicks$.pipe(
  filter((event) => event.target.dataset.leadGeneratorOpener),
  filter((event) => event.target.closest(paidCommunityCardSelector)),
  map((event) => event.target)
);

// observable created when there is a click in the list view.
// It will filter the actions to identify the ones performed in the CTA button.
const paidCustomCommunityLeadButtonClicked$ = listViewSectionClicks$.pipe(
  filter((event) => event.target.dataset.leadGeneratorOpener),
  filter((event) => event.target.closest(paidCustomCommunityCardSelector)),
  map((event) => event.target)
);

// observable created when there is a click in the list view.
// It will filter the actions to identify the ones performed in the CTA button.
const paidBuildOnYourLotCommunityLeadButtonClicked$ = listViewSectionClicks$.pipe(
  filter((event) => event.target.dataset.leadGeneratorOpener),
  filter((event) => event.target.closest(paidBuildOnYourLotCommunityCardSelector)),
  map((event) => event.target)
);

// observable created when there is a click in the list view.
// It will filter the actions to identify the ones performed in the CTA button.
const paidManufacturedCommunityLeadButtonClicked$ = listViewSectionClicks$.pipe(
  filter((event) => event.target.dataset.leadGeneratorOpener),
  filter((event) => event.target.closest(paidManufacturedCommunityCardSelector)),
  map((event) => event.target)
);

/**
 * The triggerLeadButtonClickEvent function is used to track the Lead Button Click.
 * This will gather the custom properties and dispatch the segment event
 * @param action Get the target element of the event
 */
const triggerLeadButtonClickEvent = (element) => {
  const cardElement = element.closest(paidCommunityCardSelector);
  const buttonDataset = element.dataset;
  const cardDataset = cardElement.dataset;
  const customProperties = getLeadButtonClickedCommunityCustomProperties(cardDataset, buttonDataset, true);

  setTimeout(()=> store.dispatch(segmentTrack({ eventName: 'Lead Button Clicked', customProperties })), 0);
};

/**
 * The triggerPaidCustomCommunityLeadButtonClickEvent function is used to track the Lead Button Click.
 * This will gather the custom properties and dispatch the segment event
 * @param action Get the target element of the event
 */
const triggerPaidCustomCommunityLeadButtonClickEvent = (element) => {
  const cardElement = element.closest(paidCustomCommunityCardSelector);
  const buttonDataset = element.dataset;
  const cardDataset = cardElement.dataset;
  const customProperties = getLeadButtonClickedCommunityCustomProperties(cardDataset, buttonDataset, true);

  setTimeout(()=> store.dispatch(segmentTrack({ eventName: 'Lead Button Clicked', customProperties })), 0);
};

/**
 * The triggerPaidBuildOnYourLotCommunityLeadButtonClickEvent function is used to track the Lead Button Click.
 * This will gather the custom properties and dispatch the segment event
 * @param action Get the target element of the event
 */
const triggerPaidBuildOnYourLotCommunityLeadButtonClickEvent = (element) => {
  const cardElement = element.closest(paidBuildOnYourLotCommunityCardSelector);
  const buttonDataset = element.dataset;
  const cardDataset = cardElement.dataset;
  const customProperties = getLeadButtonClickedCommunityCustomProperties(cardDataset, buttonDataset, true);

  setTimeout(()=> store.dispatch(segmentTrack({ eventName: 'Lead Button Clicked', customProperties })), 0);
};

/**
 * The triggerPaidManufacturedCommunityLeadButtonClickEvent function is used to track the Lead Button Click.
 * This will gather the custom properties and dispatch the segment event
 * @param action Get the target element of the event
 */
const triggerPaidManufacturedCommunityLeadButtonClickEvent = (element) => {
  const cardElement = element.closest(paidManufacturedCommunityCardSelector);
  const buttonDataset = element.dataset;
  const cardDataset = cardElement.dataset;
  const customProperties = getLeadButtonClickedCommunityCustomProperties(cardDataset, buttonDataset, true);

  setTimeout(()=> store.dispatch(segmentTrack({ eventName: 'Lead Button Clicked', customProperties })), 0);
};

paidCommunityLeadButtonClicked$.subscribe(triggerLeadButtonClickEvent);
paidCustomCommunityLeadButtonClicked$.subscribe(triggerPaidCustomCommunityLeadButtonClickEvent);
paidBuildOnYourLotCommunityLeadButtonClicked$.subscribe(triggerPaidBuildOnYourLotCommunityLeadButtonClickEvent);
paidManufacturedCommunityLeadButtonClicked$.subscribe(triggerPaidManufacturedCommunityLeadButtonClickEvent);

// ! Basic Community Lead Button Click Event //

// observable created when there is a click in the list view.
// It will filter the actions to identify the ones performed in the CTA button.
const basicCommunityLeadButtonClicked$ = listViewSectionClicks$.pipe(
  filter((event) => event.target.dataset.leadGeneratorOpener),
  filter((event) => event.target.closest(basicCommunityCardSelector)),
  map((event) => event.target)
);

// observable created when there is a click in the list view.
// It will filter the actions to identify the ones performed in the CTA button.
const basicBuildOnYourLotCommunityLeadButtonClicked$ = listViewSectionClicks$.pipe(
  filter((event) => event.target.dataset.leadGeneratorOpener),
  filter((event) => event.target.closest(basicBuildOnYourLotCommunityCardSelector)),
  map((event) => event.target)
);

// observable created when there is a click in the list view.
// It will filter the actions to identify the ones performed in the CTA button.
const basicCustomCommunityLeadButtonClicked$ = listViewSectionClicks$.pipe(
  filter((event) => event.target.dataset.leadGeneratorOpener),
  filter((event) => event.target.closest(basicCustomCommunityCardSelector)),
  map((event) => event.target)
);

// observable created when there is a click in the list view.
// It will filter the actions to identify the ones performed in the CTA button.
const basicManufacturedCommunityLeadButtonClicked$ = listViewSectionClicks$.pipe(
  filter((event) => event.target.dataset.leadGeneratorOpener),
  filter((event) => event.target.closest(basicManufacturedCommunityCardSelector)),
  map((event) => event.target)
);

/**
 * The triggerBasicLeadButtonClickEvent function is used to track the Lead Button Click.
 * This will gather the custom properties and dispatch the segment event
 * @param action Get the target element of the event
 */
const triggerBasicLeadButtonClickEvent = (element) => {
  const cardElement = element.closest(basicCommunityCardSelector);
  const buttonDataset = element.dataset;
  const cardDataset = cardElement.dataset;
  const customProperties = getLeadButtonClickedCommunityCustomProperties(cardDataset, buttonDataset, false);
  
  setTimeout(()=> store.dispatch(segmentTrack({ eventName: 'Lead Button Clicked', customProperties })), 0);
};

/**
 * The triggerBasicBuildOnYourLotLeadButtonClickEvent function is used to track the Lead Button Click.
 * This will gather the custom properties and dispatch the segment event
 * @param action Get the target element of the event
 */
const triggerBasicBuildOnYourLotLeadButtonClickEvent = (element) => {
  const cardElement = element.closest(basicBuildOnYourLotCommunityCardSelector);
  const buttonDataset = element.dataset;
  const cardDataset = cardElement.dataset;
  const customProperties = getLeadButtonClickedCommunityCustomProperties(cardDataset, buttonDataset, false);
  
  setTimeout(()=> store.dispatch(segmentTrack({ eventName: 'Lead Button Clicked', customProperties })), 0);
};

/**
 * The triggerBasicCustomLeadButtonClickEvent function is used to track the Lead Button Click.
 * This will gather the custom properties and dispatch the segment event
 * @param action Get the target element of the event
 */
const triggerBasicCustomLeadButtonClickEvent = (element) => {
  const cardElement = element.closest(basicCustomCommunityCardSelector);
  const buttonDataset = element.dataset;
  const cardDataset = cardElement.dataset;
  const customProperties = getLeadButtonClickedCommunityCustomProperties(cardDataset, buttonDataset, false);
  
  setTimeout(()=> store.dispatch(segmentTrack({ eventName: 'Lead Button Clicked', customProperties })), 0);
};

/**
 * The triggerBasicManufacturedLeadButtonClickEvent function is used to track the Lead Button Click.
 * This will gather the custom properties and dispatch the segment event
 * @param action Get the target element of the event
 */
const triggerBasicManufacturedLeadButtonClickEvent = (element) => {
  const cardElement = element.closest(basicManufacturedCommunityCardSelector);
  const buttonDataset = element.dataset;
  const cardDataset = cardElement.dataset;
  const customProperties = getLeadButtonClickedCommunityCustomProperties(cardDataset, buttonDataset, false);
  
  setTimeout(()=> store.dispatch(segmentTrack({ eventName: 'Lead Button Clicked', customProperties })), 0);
};

basicCommunityLeadButtonClicked$.subscribe(triggerBasicLeadButtonClickEvent);
basicBuildOnYourLotCommunityLeadButtonClicked$.subscribe(triggerBasicBuildOnYourLotLeadButtonClickEvent);
basicCustomCommunityLeadButtonClicked$.subscribe(triggerBasicCustomLeadButtonClickEvent);
basicManufacturedCommunityLeadButtonClicked$.subscribe(triggerBasicManufacturedLeadButtonClickEvent);

// ! Community Result Selected Event //

// observable created when there is a click in the list view.
// It will filter the actions to identify the ones performed in the card which takes the user to detail page.
// filters the clicks on lead cta and fav button.
const paidcommunityCardClicked$ = listViewSectionClicks$.pipe(
  filter((event) => event.target.closest(paidCommunityCardSelector)),
  filter((event) => !event.target.dataset?.leadGeneratorOpener),
  filter((event) => !event.target.closest(cardfavoriteButtonSelector)),
  map((event) => event.target)
);

/**
 * The triggerCommunityCardClickEvent function is used to track the card Click.
 * This will gather the custom properties and dispatch the segment event
 * @param action Get the target element of the event
 *
 */
const triggerCommunityCardClickEvent = (element) => {
  const cardElement = element.closest(paidCommunityCardSelector);
  const cardDataset = cardElement.dataset;
  const elementName = element.dataset.cardElement;
  const cardAreaClicked = segmentAreaClickedPerCardElement[elementName] || segmentAreaClickedPerCardElement.default;
  const cardImages = getCardImageUrls(cardElement);
  const position = parseInt(cardDataset.index, 10) + 1;

  const customProperties = {
    market_id: cardDataset.marketId,
    market_name: `${cardDataset.marketName}, ${cardDataset.stateAbbreviation}`,
    city: cardDataset.city,
    state: cardDataset.stateAbbreviation,
    zip: cardDataset.zip,
    builder_id: cardDataset.builderId,
    builder_name: cardDataset.brandName,
    community_id: cardDataset.id,
    community_name: cardDataset.communityName,
    community_status: cardDataset.marketingStatusLabel,
    primary_image_url: cardDataset.imageUrl,
    community_low_price: cardDataset.priceLow,
    community_high_price: cardDataset.priceHigh,
    position,
    area_clicked: cardAreaClicked,
    image_1_url: cardImages.primaryImage,
    image_2_url: cardImages.secondaryImage,
    image_3_url: cardImages.terciaryImage,
    is_paid: true,
    is_spotlight: !!cardDataset.wasMatchedAsSpotlightResult,
    community_type: cardDataset.communityType,
  };

  store.dispatch(segmentTrack({ eventName: 'Community Result Selected', customProperties }));
};

paidcommunityCardClicked$.subscribe(triggerCommunityCardClickEvent);

// ! Community Result Selected Event: Basic Communities //

// observable created when there is a click in the list view.
// It will filter the actions to identify the ones performed in the card which takes the user to detail page.
// filters the clicks on lead cta and fav button.
const basicCommunityCardClicked$ = listViewSectionClicks$.pipe(
  filter((event) => event.target.closest(basicCommunityCardSelector)),
  filter((event) => !event.target.dataset?.leadGeneratorOpener),
  filter((event) => !event.target.closest(cardfavoriteButtonSelector)),
  map((event) => event.target)
);

/**
 * The triggerBasicCommunityCardClickEvent function is used to track the card click for Basic Communities.
 * This will gather the custom properties and dispatch the segment event
 * @param action Get the target element of the event
 *
 */
const triggerBasicCommunityCardClickEvent = (element) => {
  const cardElement = element.closest(basicCommunityCardSelector);
  const cardDataset = cardElement.dataset;
  const elementName = element.dataset.cardElement;
  const cardAreaClicked = segmentAreaClickedPerCardElement[elementName] || segmentAreaClickedPerCardElement.default;
  const cardImages = getCardImageUrls(cardElement);
  const position = parseInt(cardDataset.index, 10) + 1;

  const customProperties = {
    market_id: cardDataset.marketId,
    market_name: `${cardDataset.marketName}, ${cardDataset.stateAbbreviation}`,
    city: cardDataset.city,
    state: cardDataset.stateAbbreviation,
    zip: cardDataset.zip,
    builder_id: cardDataset.builderId,
    builder_name: cardDataset.brandName,
    community_id: cardDataset.id,
    community_name: cardDataset.communityName,
    is_paid: false,
    primary_image_url: cardDataset.imageUrl,
    community_low_price: cardDataset.priceLow,
    community_high_price: cardDataset.priceHigh,
    position,
    area_clicked: cardAreaClicked,
    image_1_url: cardImages.primaryImage,
    image_2_url: cardImages.secondaryImage,
    image_3_url: cardImages.terciaryImage,
    is_spotlight: !!cardDataset.wasMatchedAsSpotlightResult,
    community_type: cardDataset.communityType,
  };

  store.dispatch(segmentTrack({ eventName: 'Community Result Selected', customProperties }));
};

basicCommunityCardClicked$.subscribe(triggerBasicCommunityCardClickEvent);

// ! Community Favorited Event //

// observable created when there is an add to the redux store object "pendingSaves".
// this object contains a list of items that the user clicked but are not already saved.
// It will filter the actions to now when it changes.
const onPendingFavoritesChanged$ = store$.pipe(
  map(selectPendingSaves),
  filter(Boolean),
  distinctUntilChanged(),
  pairwise()
);

/**
 * The triggerCommunityFavoritedEvent function is used to track the favorited event.
 * which happens when the user clicks in the fav button to save it.
 * This will gather the custom properties and dispatch the segment event
 * @param action Get the target element of the event
 *
 */
const triggerCommunityFavoritedEvent = (payload) => {
  const [lastValue, newValue] = payload;

  // this filter, validates if it was an item addition to the existing array, and works with the addedd item
  const newFavoritedItems = newValue.filter((item) => !lastValue.includes(item));
  if (!newFavoritedItems || newFavoritedItems.length === 0) {
    return;
  }

  const itemId = newFavoritedItems[0].listingInformation.id;
  const typeCard = newFavoritedItems[0].listingInformation.isBasic === 'true'
    ? 'basic' : 'paid';
  const cardElement = document.querySelector(`[data-${typeCard}-community][data-id="${itemId}"]`);

  if (!cardElement) {
    return;
  }

  const cardDataset = cardElement.dataset;
  const isPaid = cardElement.dataset.isBasic !== 'true';
  const customProperties = {
    market_id: cardDataset.marketId,
    market_name: `${cardDataset.marketName}, ${cardDataset.stateAbbreviation}`,
    city: cardDataset.city,
    state: cardDataset.stateAbbreviation,
    zip: cardDataset.zip,
    builder_id: cardDataset.builderId,
    builder_name: cardDataset.brandName,
    community_id: cardDataset.id,
    community_name: cardDataset.communityName,
    community_status: cardDataset.marketingStatusLabel,
    primary_image_url: cardDataset.imageUrl,
    community_low_price: cardDataset.priceLow,
    community_high_price: cardDataset.priceHigh,
    is_paid: isPaid,
    is_spotlight: !!cardDataset.wasMatchedAsSpotlightResult,
    community_type: cardDataset.communityType,
    is_neighborhood: isNeighborhoodPage(),
    is_schooldistrict: isSchoolDistrictPage(),
  };

  store.dispatch(segmentTrack({ eventName: 'Community Favorited', customProperties }));
};

onPendingFavoritesChanged$.subscribe(triggerCommunityFavoritedEvent);

// ! Home (Spec/Plan) Lead Button Click Event //
/**
 * The getLeadButtonClickedPlanCustomProperties function is used to get the custom properties
 * for plans for the event LeadButtonClicked
 * @param cardElement The card element
 * @param buttonDataset The button dataset
 * @param isPaid Bool that define if the community is paid or basic
 */
const getLeadButtonClickedHomesCustomProperties = (cardElement, buttonDataset, isPaid) => {
  const cardDataset = cardElement.dataset;
  const primaryImageUrl =
    cardDataset.imageUrl !== '' ? cardDataset.imageUrl : getCardImageUrls(cardElement).primaryImage;

  const customProperties = {
    market_id: cardDataset.marketId,
    market_name: `${cardDataset.marketName}, ${cardDataset.stateAbbreviation}`,
    city: cardDataset.city,
    state: cardDataset.stateAbbreviation,
    zip: cardDataset.zip,
    builder_id: cardDataset.builderId,
    builder_name: cardDataset.brandName,
    community_id: cardDataset.communityId,
    community_name: cardDataset.communityName,
    primary_image_url: primaryImageUrl,
    button_action: buttonDataset.buttonAction,
    plan_id: cardDataset.planId,
    spec_id: cardDataset.specId,
    home_type: cardDataset.homeStatus,
    home_price: cardDataset.price,
    is_paid: isPaid,
    is_spotlight: !!cardDataset.wasMatchedAsSpotlightResult,
    community_type: cardDataset.communityType,
    is_neighborhood: isNeighborhoodPage(),
    is_schooldistrict: isSchoolDistrictPage(),
    position: +cardDataset.index + 1,
  };

  customProperties[
    cardDataset.type === "spec" ? "specName" : "planName"
  ] = cardDataset.name;

  return customProperties;
};



// observable created when there is a click in the list view.
// It will filter the actions to identify the ones performed in the CTA button.
const paidPlanLeadButtonClicked$ = listViewSectionClicks$.pipe(
  filter((event) => event.target.dataset.leadGeneratorOpener),
  filter((event) => event.target.closest(paidPlanCardSelector)),
  map((event) => event.target)
);

// observable created when there is a click in the list view.
// It will filter the actions to identify the ones performed in the CTA button.
const paidSpecLeadButtonClicked$ = listViewSectionClicks$.pipe(
  filter((event) => event.target.dataset.leadGeneratorOpener),
  filter((event) => event.target.closest(paidSpecCardSelector)),
  map((event) => event.target)
);

// observable created when there is a click in the list view.
// It will filter the actions to identify the ones performed in the CTA button.
const paidManufacturedPlanLeadButtonClicked$ = listViewSectionClicks$.pipe(
  filter((event) => event.target.dataset.leadGeneratorOpener),
  filter((event) => event.target.closest(paidManufacturedPlanCardSelector)),
  map((event) => event.target)
);

// observable created when there is a click in the list view.
// It will filter the actions to identify the ones performed in the CTA button.
const paidManufacturedSpecLeadButtonClicked$ = listViewSectionClicks$.pipe(
  filter((event) => event.target.dataset.leadGeneratorOpener),
  filter((event) => event.target.closest(paidManufacturedSpecCardSelector)),
  map((event) => event.target)
);

// observable created when there is a click in the list view.
// It will filter the actions to identify the ones performed in the CTA button.
const paidCustomPlanLeadButtonClicked$ = listViewSectionClicks$.pipe(
  filter((event) => event.target.dataset.leadGeneratorOpener),
  filter((event) => event.target.closest(paidCustomPlanCardSelector)),
  map((event) => event.target)
);

// observable created when there is a click in the list view.
// It will filter the actions to identify the ones performed in the CTA button.
const paidCustomSpecLeadButtonClicked$ = listViewSectionClicks$.pipe(
  filter((event) => event.target.dataset.leadGeneratorOpener),
  filter((event) => event.target.closest(paidCustomSpecCardSelector)),
  map((event) => event.target)
);

// observable created when there is a click in the list view.
// It will filter the actions to identify the ones performed in the CTA button.
const paidBuildOnYourLotPlanLeadButtonClicked$ = listViewSectionClicks$.pipe(
  filter((event) => event.target.dataset.leadGeneratorOpener),
  filter((event) => event.target.closest(paidBuildOnYourLotPlanCardSelector)),
  map((event) => event.target)
);

// observable created when there is a click in the list view.
// It will filter the actions to identify the ones performed in the CTA button.
const paidBuildOnYourLotSpecLeadButtonClicked$ = listViewSectionClicks$.pipe(
  filter((event) => event.target.dataset.leadGeneratorOpener),
  filter((event) => event.target.closest(paidBuildOnYourLotSpecCardSelector)),
  map((event) => event.target)
);

/**
 * The triggerPlanLeadButtonClickEvent function is used to track the Lead Button Click.
 * This will gather the custom properties and dispatch the segment event
 * @param element Get the target element of the event
 */
const triggerPlanLeadButtonClickEvent = (element) => {
  const cardElement = element.closest(paidPlanCardSelector);
  const buttonDataset = element.dataset;
  const customProperties = getLeadButtonClickedHomesCustomProperties(cardElement, buttonDataset, true);

  setTimeout(()=> store.dispatch(segmentTrack({ eventName: 'Lead Button Clicked', customProperties })), 0);
};

/**
 * The triggerSpecLeadButtonClickEvent function is used to track the Lead Button Click.
 * This will gather the custom properties and dispatch the segment event
 * @param element Get the target element of the event
 */
const triggerSpecLeadButtonClickEvent = (element) => {
  const cardElement = element.closest(paidSpecCardSelector);
  const buttonDataset = element.dataset;
  const customProperties = getLeadButtonClickedHomesCustomProperties(cardElement, buttonDataset, true);

  setTimeout(()=> store.dispatch(segmentTrack({ eventName: 'Lead Button Clicked', customProperties })), 0);
};

/**
 * The triggerManufacturedPlanLeadButtonClickEvent function is used to track the Lead Button Click.
 * This will gather the custom properties and dispatch the segment event
 * @param element Get the target element of the event
 */
const triggerManufacturedPlanLeadButtonClickEvent = (element) => {
  const cardElement = element.closest(paidManufacturedPlanCardSelector);
  const buttonDataset = element.dataset;
  const customProperties = getLeadButtonClickedHomesCustomProperties(cardElement, buttonDataset, true);

  setTimeout(()=> store.dispatch(segmentTrack({ eventName: 'Lead Button Clicked', customProperties })), 0);
};

/**
 * The triggerManufacturedSpecLeadButtonClickEvent function is used to track the Lead Button Click.
 * This will gather the custom properties and dispatch the segment event
 * @param element Get the target element of the event
 */
const triggerManufacturedSpecLeadButtonClickEvent = (element) => {
  const cardElement = element.closest(paidManufacturedSpecCardSelector);
  const buttonDataset = element.dataset;
  const customProperties = getLeadButtonClickedHomesCustomProperties(cardElement, buttonDataset, true);

  setTimeout(()=> store.dispatch(segmentTrack({ eventName: 'Lead Button Clicked', customProperties })), 0);
};

/**
 * The triggerCustomPlanLeadButtonClickEvent function is used to track the Lead Button Click.
 * This will gather the custom properties and dispatch the segment event
 * @param element Get the target element of the event
 */
const triggerCustomPlanLeadButtonClickEvent = (element) => {
  const cardElement = element.closest(paidCustomPlanCardSelector);
  const buttonDataset = element.dataset;
  const customProperties = getLeadButtonClickedHomesCustomProperties(cardElement, buttonDataset, true);

  setTimeout(()=> store.dispatch(segmentTrack({ eventName: 'Lead Button Clicked', customProperties })), 0);
};

/**
 * The triggerCustomSpecLeadButtonClickEvent function is used to track the Lead Button Click.
 * This will gather the custom properties and dispatch the segment event
 * @param element Get the target element of the event
 */
const triggerCustomSpecLeadButtonClickEvent = (element) => {
  const cardElement = element.closest(paidCustomSpecCardSelector);
  const buttonDataset = element.dataset;
  const customProperties = getLeadButtonClickedHomesCustomProperties(cardElement, buttonDataset, true);

  setTimeout(()=> store.dispatch(segmentTrack({ eventName: 'Lead Button Clicked', customProperties })), 0);
};

/**
 * The triggerBuildOnYourLotPlanLeadButtonClickEvent function is used to track the Lead Button Click.
 * This will gather the custom properties and dispatch the segment event
 * @param element Get the target element of the event
 */
const triggerBuildOnYourLotPlanLeadButtonClickEvent = (element) => {
  const cardElement = element.closest(paidBuildOnYourLotPlanCardSelector);
  const buttonDataset = element.dataset;
  const customProperties = getLeadButtonClickedHomesCustomProperties(cardElement, buttonDataset, true);

  setTimeout(()=> store.dispatch(segmentTrack({ eventName: 'Lead Button Clicked', customProperties })), 0);
};

/**
 * The triggerBuildOnYourLotSpecLeadButtonClickEvent function is used to track the Lead Button Click.
 * This will gather the custom properties and dispatch the segment event
 * @param element Get the target element of the event
 */
const triggerBuildOnYourLotSpecLeadButtonClickEvent = (element) => {
  const cardElement = element.closest(paidBuildOnYourLotSpecCardSelector);
  const buttonDataset = element.dataset;
  const customProperties = getLeadButtonClickedHomesCustomProperties(cardElement, buttonDataset, true);

  setTimeout(()=> store.dispatch(segmentTrack({ eventName: 'Lead Button Clicked', customProperties })), 0);
};

paidPlanLeadButtonClicked$.subscribe(triggerPlanLeadButtonClickEvent);
paidSpecLeadButtonClicked$.subscribe(triggerSpecLeadButtonClickEvent);
paidManufacturedPlanLeadButtonClicked$.subscribe(triggerManufacturedPlanLeadButtonClickEvent);
paidManufacturedSpecLeadButtonClicked$.subscribe(triggerManufacturedSpecLeadButtonClickEvent);
paidCustomPlanLeadButtonClicked$.subscribe(triggerCustomPlanLeadButtonClickEvent);
paidCustomSpecLeadButtonClicked$.subscribe(triggerCustomSpecLeadButtonClickEvent);
paidBuildOnYourLotPlanLeadButtonClicked$.subscribe(triggerBuildOnYourLotPlanLeadButtonClickEvent);
paidBuildOnYourLotSpecLeadButtonClicked$.subscribe(triggerBuildOnYourLotSpecLeadButtonClickEvent);

// ! Basic Home (Spec/Plan/Listing) Lead Button Click Event //

// observable created when there is a click in the list view.
// It will filter the actions to identify the ones performed in the CTA button.
const basicPlanLeadButtonClicked$ = listViewSectionClicks$.pipe(
  filter((event) => event.target.dataset.leadGeneratorOpener),
  filter((event) => event.target.closest(basicPlanCardSelector)),
  map((event) => event.target)
);

// observable created when there is a click in the list view.
// It will filter the actions to identify the ones performed in the CTA button.
const basicSpecLeadButtonClicked$ = listViewSectionClicks$.pipe(
  filter((event) => event.target.dataset.leadGeneratorOpener),
  filter((event) => event.target.closest(basicSpecCardSelector)),
  map((event) => event.target)
);

// observable created when there is a click in the list view.
// It will filter the actions to identify the ones performed in the CTA button.
const basicManufacturedPlanLeadButtonClicked$ = listViewSectionClicks$.pipe(
  filter((event) => event.target.dataset.leadGeneratorOpener),
  filter((event) => event.target.closest(basicManufacturedPlanCardSelector)),
  map((event) => event.target)
);

// observable created when there is a click in the list view.
// It will filter the actions to identify the ones performed in the CTA button.
const basicManufacturedSpecLeadButtonClicked$ = listViewSectionClicks$.pipe(
  filter((event) => event.target.dataset.leadGeneratorOpener),
  filter((event) => event.target.closest(basicManufacturedSpecCardSelector)),
  map((event) => event.target)
);

// observable created when there is a click in the list view.
// It will filter the actions to identify the ones performed in the CTA button.
const basicCustomPlanLeadButtonClicked$ = listViewSectionClicks$.pipe(
  filter((event) => event.target.dataset.leadGeneratorOpener),
  filter((event) => event.target.closest(basicCustomPlanCardSelector)),
  map((event) => event.target)
);

// observable created when there is a click in the list view.
// It will filter the actions to identify the ones performed in the CTA button.
const basicCustomSpecLeadButtonClicked$ = listViewSectionClicks$.pipe(
  filter((event) => event.target.dataset.leadGeneratorOpener),
  filter((event) => event.target.closest(basicCustomSpecCardSelector)),
  map((event) => event.target)
);

// observable created when there is a click in the list view.
// It will filter the actions to identify the ones performed in the CTA button.
const basicBuildOnYourLotPlanLeadButtonClicked$ = listViewSectionClicks$.pipe(
  filter((event) => event.target.dataset.leadGeneratorOpener),
  filter((event) => event.target.closest(basicBuildOnYourLotPlanCardSelector)),
  map((event) => event.target)
);

// observable created when there is a click in the list view.
// It will filter the actions to identify the ones performed in the CTA button.
const basicBuildOnYourLotSpecLeadButtonClicked$ = listViewSectionClicks$.pipe(
  filter((event) => event.target.dataset.leadGeneratorOpener),
  filter((event) => event.target.closest(basicBuildOnYourLotSpecCardSelector)),
  map((event) => event.target)
);

// observable created when there is a click in the list view.
// It will filter the actions to identify the ones performed in the CTA button.
const basicListingLeadButtonClicked$ = listViewSectionClicks$.pipe(
  filter((event) => event.target.dataset.leadGeneratorOpener),
  filter((event) => event.target.closest(basicListingCardSelector)),
  map((event) => event.target)
);

/**
 * The triggerBasicPlanLeadButtonClickEvent function is used to track the Lead Button Click.
 * This will gather the custom properties and dispatch the segment event
 * @param element Get the target element of the event
 */
const triggerBasicPlanLeadButtonClickEvent = (element) => {
  const cardElement = element.closest(basicPlanCardSelector);
  const buttonDataset = element.dataset;
  const customProperties = getLeadButtonClickedHomesCustomProperties(cardElement, buttonDataset, false);

  setTimeout(()=> store.dispatch(segmentTrack({ eventName: 'Lead Button Clicked', customProperties })), 0);
};

/**
 * The triggerBasicSpecLeadButtonClickEvent function is used to track the Lead Button Click.
 * This will gather the custom properties and dispatch the segment event
 * @param element Get the target element of the event
 */
const triggerBasicSpecLeadButtonClickEvent = (element) => {
  const cardElement = element.closest(basicSpecCardSelector);
  const buttonDataset = element.dataset;
  const customProperties = getLeadButtonClickedHomesCustomProperties(cardElement, buttonDataset, false);

  setTimeout(()=> store.dispatch(segmentTrack({ eventName: 'Lead Button Clicked', customProperties })), 0);
};

/**
 * The triggerBasicManufacturedPlanLeadButtonClickEvent function is used to track the Lead Button Click.
 * This will gather the custom properties and dispatch the segment event
 * @param element Get the target element of the event
 */
const triggerBasicManufacturedPlanLeadButtonClickEvent = (element) => {
  const cardElement = element.closest(basicManufacturedPlanCardSelector);
  const buttonDataset = element.dataset;
  const customProperties = getLeadButtonClickedHomesCustomProperties(cardElement, buttonDataset, false);

  setTimeout(()=> store.dispatch(segmentTrack({ eventName: 'Lead Button Clicked', customProperties })), 0);
};

/**
 * The triggerBasicManufacturedSpecLeadButtonClickEvent function is used to track the Lead Button Click.
 * This will gather the custom properties and dispatch the segment event
 * @param element Get the target element of the event
 */
const triggerBasicManufacturedSpecLeadButtonClickEvent = (element) => {
  const cardElement = element.closest(basicManufacturedSpecCardSelector);
  const buttonDataset = element.dataset;
  const customProperties = getLeadButtonClickedHomesCustomProperties(cardElement, buttonDataset, false);

  setTimeout(()=> store.dispatch(segmentTrack({ eventName: 'Lead Button Clicked', customProperties })), 0);
};

/**
 * The triggerBasicCustomPlanLeadButtonClickEvent function is used to track the Lead Button Click.
 * This will gather the custom properties and dispatch the segment event
 * @param element Get the target element of the event
 */
const triggerBasicCustomPlanLeadButtonClickEvent = (element) => {
  const cardElement = element.closest(basicCustomPlanCardSelector);
  const buttonDataset = element.dataset;
  const customProperties = getLeadButtonClickedHomesCustomProperties(cardElement, buttonDataset, false);

  setTimeout(()=> store.dispatch(segmentTrack({ eventName: 'Lead Button Clicked', customProperties })), 0);
};

/**
 * The triggerBasicCustomSpecLeadButtonClickEvent function is used to track the Lead Button Click.
 * This will gather the custom properties and dispatch the segment event
 * @param element Get the target element of the event
 */
const triggerBasicCustomSpecLeadButtonClickEvent = (element) => {
  const cardElement = element.closest(basicCustomSpecCardSelector);
  const buttonDataset = element.dataset;
  const customProperties = getLeadButtonClickedHomesCustomProperties(cardElement, buttonDataset, false);

  setTimeout(()=> store.dispatch(segmentTrack({ eventName: 'Lead Button Clicked', customProperties })), 0);
};

/**
 * The triggerBasicBuildOnYourLotPlanLeadButtonClickEvent function is used to track the Lead Button Click.
 * This will gather the custom properties and dispatch the segment event
 * @param element Get the target element of the event
 */
const triggerBasicBuildOnYourLotPlanLeadButtonClickEvent = (element) => {
  const cardElement = element.closest(basicBuildOnYourLotPlanCardSelector);
  const buttonDataset = element.dataset;
  const customProperties = getLeadButtonClickedHomesCustomProperties(cardElement, buttonDataset, false);

  setTimeout(()=> store.dispatch(segmentTrack({ eventName: 'Lead Button Clicked', customProperties })), 0);
};

/**
 * The triggerBasicBuildOnYourLotSpecLeadButtonClickEvent function is used to track the Lead Button Click.
 * This will gather the custom properties and dispatch the segment event
 * @param element Get the target element of the event
 */
const triggerBasicBuildOnYourLotSpecLeadButtonClickEvent = (element) => {
  const cardElement = element.closest(basicBuildOnYourLotSpecCardSelector);
  const buttonDataset = element.dataset;
  const customProperties = getLeadButtonClickedHomesCustomProperties(cardElement, buttonDataset, false);

  setTimeout(()=> store.dispatch(segmentTrack({ eventName: 'Lead Button Clicked', customProperties })), 0);
};
/**
 * The triggerBasicListingLeadButtonClickEvent function is used to track the Lead Button Click.
 * This will gather the custom properties and dispatch the segment event
 * @param element Get the target element of the event
 */
const triggerBasicListingLeadButtonClickEvent = (element) => {
  const cardElement = element.closest(basicListingCardSelector);
  const buttonDataset = element.dataset;
  const cardDataset = cardElement.dataset;

  const primaryImageUrl =
    cardDataset.imageUrl !== '' ? cardDataset.imageUrl : getCardImageUrls(cardElement).primaryImage;

  const customProperties = {
    market_id: cardDataset.marketId,
    market_name: `${cardDataset.marketName}, ${cardDataset.stateAbbreviation}`,
    city: cardDataset.city,
    state: cardDataset.stateAbbreviation,
    zip: cardDataset.zip,
    primary_image_url: primaryImageUrl,
    button_action: buttonDataset.buttonAction,
    listing_id: cardDataset.id,
    home_price: cardDataset.price,
    is_paid: false,
    is_neighborhood: isNeighborhoodPage(),
    is_schooldistrict: isSchoolDistrictPage(),
  };

  setTimeout(()=> store.dispatch(segmentTrack({ eventName: 'Lead Button Clicked', customProperties })), 0);
};

basicPlanLeadButtonClicked$.subscribe(triggerBasicPlanLeadButtonClickEvent);
basicSpecLeadButtonClicked$.subscribe(triggerBasicSpecLeadButtonClickEvent);
basicManufacturedPlanLeadButtonClicked$.subscribe(triggerBasicManufacturedPlanLeadButtonClickEvent);
basicManufacturedSpecLeadButtonClicked$.subscribe(triggerBasicManufacturedSpecLeadButtonClickEvent);
basicCustomPlanLeadButtonClicked$.subscribe(triggerBasicCustomPlanLeadButtonClickEvent);
basicCustomSpecLeadButtonClicked$.subscribe(triggerBasicCustomSpecLeadButtonClickEvent);
basicBuildOnYourLotPlanLeadButtonClicked$.subscribe(triggerBasicBuildOnYourLotPlanLeadButtonClickEvent);
basicBuildOnYourLotSpecLeadButtonClicked$.subscribe(triggerBasicBuildOnYourLotSpecLeadButtonClickEvent);
basicListingLeadButtonClicked$.subscribe(triggerBasicListingLeadButtonClickEvent);

// ! Home Result Selected Event (spec / plan) //

// observable created when there is a click in the list view.
// It will filter the actions to identify the ones performed in the card which takes the user to detail page.
// filters the clicks on lead cta and fav button.
const paidPlanCardClicked$ = listViewSectionClicks$.pipe(
  filter((event) => event.target.closest(paidPlanCardSelector)),
  filter((event) => !event.target.dataset?.leadGeneratorOpener),
  filter((event) => !event.target.closest(cardfavoriteButtonSelector)),
  map((event) => event.target)
);

// observable created when there is a click in the list view.
// It will filter the actions to identify the ones performed in the card which takes the user to detail page.
// filters the clicks on lead cta and fav button.
const paidSpecCardClicked$ = listViewSectionClicks$.pipe(
  filter((event) => event.target.closest(paidSpecCardSelector)),
  filter((event) => !event.target.dataset?.leadGeneratorOpener),
  filter((event) => !event.target.closest(cardfavoriteButtonSelector)),
  map((event) => event.target)
);

// observable created when there is a click in the list view.
// It will filter the actions to identify the ones performed in the card which takes the user to detail page.
// filters the clicks on lead cta and fav button.
const manufacturedPlanCardClicked$ = listViewSectionClicks$.pipe(
  filter((event) => event.target.closest(paidManufacturedPlanCardSelector) ||
    event.target.closest(basicManufacturedPlanCardSelector)),
  filter((event) => !event.target.dataset?.leadGeneratorOpener),
  filter((event) => !event.target.closest(cardfavoriteButtonSelector)),
  map((event) => event.target)
);

// observable created when there is a click in the list view.
// It will filter the actions to identify the ones performed in the card which takes the user to detail page.
// filters the clicks on lead cta and fav button.
const manufacturedSpecCardClicked$ = listViewSectionClicks$.pipe(
  filter((event) => event.target.closest(paidManufacturedSpecCardSelector) ||
    event.target.closest(basicManufacturedSpecCardSelector)),
  filter((event) => !event.target.dataset?.leadGeneratorOpener),
  filter((event) => !event.target.closest(cardfavoriteButtonSelector)),
  map((event) => event.target)
);
// observable created when there is a click in the list view.
// It will filter the actions to identify the ones performed in the card which takes the user to detail page.
// filters the clicks on lead cta and fav button.
const paidCustomPlanCardClicked$ = listViewSectionClicks$.pipe(
  filter((event) => event.target.closest(paidCustomPlanCardSelector)),
  filter((event) => !event.target.dataset?.leadGeneratorOpener),
  filter((event) => !event.target.closest(cardfavoriteButtonSelector)),
  map((event) => event.target)
);

// observable created when there is a click in the list view.
// It will filter the actions to identify the ones performed in the card which takes the user to detail page.
// filters the clicks on lead cta and fav button.
const basicListingCardClicked$ = listViewSectionClicks$.pipe(
  filter((event) => event.target.closest(basicListingCardSelector)),
  filter((event) => !event.target.dataset?.leadGeneratorOpener),
  filter((event) => !event.target.closest(cardfavoriteButtonSelector)),
  map((event) => event.target)
);

// observable created when there is a click in the list view.
// It will filter the actions to identify the ones performed in the card which takes the user to detail page.
// filters the clicks on lead cta and fav button.
const basicPlanCardClicked$ = listViewSectionClicks$.pipe(
  filter((event) => event.target.closest(basicPlanCardSelector)),
  filter((event) => !event.target.dataset?.leadGeneratorOpener),
  filter((event) => !event.target.closest(cardfavoriteButtonSelector)),
  map((event) => event.target)
);

// observable created when there is a click in the list view.
// It will filter the actions to identify the ones performed in the card which takes the user to detail page.
// filters the clicks on lead cta and fav button.
const basicSpecCardClicked$ = listViewSectionClicks$.pipe(
  filter((event) => event.target.closest(basicSpecCardSelector)),
  filter((event) => !event.target.dataset?.leadGeneratorOpener),
  filter((event) => !event.target.closest(cardfavoriteButtonSelector)),
  map((event) => event.target)
);

/**
 * The triggerPlanCardClickEvent function is used to track the card Click.
 * This will gather the custom properties and dispatch the segment event
 * @param action Get the target element of the event
 *
 */
const triggerPlanCardClickEvent = (element) => {
  const cardElement = element.closest(paidPlanCardSelector);
  const cardDataset = cardElement.dataset;
  const elementName = element.dataset.cardElement;
  const cardAreaClicked = segmentAreaClickedPerCardElement[elementName] || segmentAreaClickedPerCardElement.default;
  const cardImages = getCardImageUrls(cardElement);
  const position = parseInt(cardDataset.index, 10) + 1;

  const customProperties = {
    market_id: cardDataset.marketId,
    market_name: `${cardDataset.marketName}, ${cardDataset.stateAbbreviation}`,
    city: cardDataset.city,
    state: cardDataset.stateAbbreviation,
    zip: cardDataset.zip,
    builder_id: cardDataset.builderId,
    builder_name: cardDataset.brandName,
    community_id: cardDataset.communityId,
    community_name: cardDataset.communityName,
    primary_image_url: cardImages.primaryImage,
    plan_id: cardDataset.planId,
    plan_name: cardDataset.name,
    home_type: cardDataset.homeStatus,
    home_price: cardDataset.price,
    position,
    area_clicked: cardAreaClicked,
    image_1_url: cardImages.primaryImage,
    is_paid: true,
    is_spotlight: !!cardDataset.wasMatchedAsSpotlightResult,
    community_type: cardDataset.communityType,
  };

  store.dispatch(segmentTrack({ eventName: 'Home Result Selected', customProperties }));
};

/**
 * The triggerSpecCardClickEvent function is used to track the card Click.
 * This will gather the custom properties and dispatch the segment event
 * @param action Get the target element of the event
 *
 */
const triggerSpecCardClickEvent = (element) => {
  const cardElement = element.closest(paidSpecCardSelector);
  const cardDataset = cardElement.dataset;
  const elementName = element.dataset.cardElement;
  const cardAreaClicked = segmentAreaClickedPerCardElement[elementName] || segmentAreaClickedPerCardElement.default;
  const cardImages = getCardImageUrls(cardElement);
  const position = parseInt(cardDataset.index, 10) + 1;

  const customProperties = {
    market_id: cardDataset.marketId,
    market_name: `${cardDataset.marketName}, ${cardDataset.stateAbbreviation}`,
    city: cardDataset.city,
    state: cardDataset.stateAbbreviation,
    zip: cardDataset.zip,
    builder_id: cardDataset.builderId,
    builder_name: cardDataset.brandName,
    community_id: cardDataset.communityId,
    community_name: cardDataset.communityName,
    primary_image_url: cardImages.primaryImage,
    spec_id: cardDataset.specId,
    spec_name: cardDataset.name,
    plan_id: cardDataset.planId,
    home_type: cardDataset.homeStatus,
    home_price: cardDataset.price,
    position,
    area_clicked: cardAreaClicked,
    image_1_url: cardImages.primaryImage,
    is_paid: true,
    is_spotlight: !!cardDataset.wasMatchedAsSpotlightResult,
    community_type: cardDataset.communityType,
  };

  store.dispatch(segmentTrack({ eventName: 'Home Result Selected', customProperties }));
};

/**
 * The triggerManufacturedPlanCardClickEvent function is used to track the card Click.
 * This will gather the custom properties and dispatch the segment event
 * @param action Get the target element of the event
 *
 */
const triggerManufacturedPlanCardClickEvent = (element) => {
  const cardElement = element.closest(paidManufacturedPlanCardSelector) ||
    element.closest(basicManufacturedPlanCardSelector);
  const cardDataset = cardElement.dataset;
  const elementName = element.dataset.cardElement;
  const cardAreaClicked = segmentAreaClickedPerCardElement[elementName] || segmentAreaClickedPerCardElement.default;
  const cardImages = getCardImageUrls(cardElement);
  const position = parseInt(cardDataset.index, 10) + 1;

  const customProperties = {
    market_id: cardDataset.marketId,
    market_name: `${cardDataset.marketName}, ${cardDataset.stateAbbreviation}`,
    city: cardDataset.city,
    state: cardDataset.stateAbbreviation,
    zip: cardDataset.zip,
    builder_id: cardDataset.builderId,
    builder_name: cardDataset.brandName,
    community_id: cardDataset.communityId,
    community_name: cardDataset.communityName,
    primary_image_url: cardImages.primaryImage,
    plan_id: cardDataset.planId,
    plan_name: cardDataset.name,
    home_type: cardDataset.homeStatus,
    home_price: cardDataset.price,
    position,
    area_clicked: cardAreaClicked,
    image_1_url: cardImages.primaryImage,
    is_paid: cardDataset.isBasic !== 'true',
    is_spotlight: !!cardDataset.wasMatchedAsSpotlightResult,
    community_type: cardDataset.communityType,
  };

  store.dispatch(segmentTrack({ eventName: 'Home Result Selected', customProperties }));
};

/**
 * The triggerManufacturedPlanCardClickEvent function is used to track the card Click.
 * This will gather the custom properties and dispatch the segment event
 * @param action Get the target element of the event
 *
 */
const triggerManufacturedSpecCardClickEvent = (element) => {
  const cardElement = element.closest(paidManufacturedSpecCardSelector) ||
    element.closest(basicManufacturedSpecCardSelector);
  const cardDataset = cardElement.dataset;
  const elementName = element.dataset.cardElement;
  const cardAreaClicked = segmentAreaClickedPerCardElement[elementName] || segmentAreaClickedPerCardElement.default;
  const cardImages = getCardImageUrls(cardElement);
  const position = parseInt(cardDataset.index, 10) + 1;

  const customProperties = {
    market_id: cardDataset.marketId,
    market_name: `${cardDataset.marketName}, ${cardDataset.stateAbbreviation}`,
    city: cardDataset.city,
    state: cardDataset.stateAbbreviation,
    zip: cardDataset.zip,
    builder_id: cardDataset.builderId,
    builder_name: cardDataset.brandName,
    community_id: cardDataset.communityId,
    community_name: cardDataset.communityName,
    primary_image_url: cardImages.primaryImage,
    spec_id: cardDataset.specId,
    home_type: cardDataset.homeStatus,
    home_price: cardDataset.price,
    position,
    area_clicked: cardAreaClicked,
    image_1_url: cardImages.primaryImage,
    is_paid: cardDataset.isBasic !== 'true',
    is_spotlight: !!cardDataset.wasMatchedAsSpotlightResult,
    community_type: cardDataset.communityType,
  };

  store.dispatch(segmentTrack({ eventName: 'Home Result Selected', customProperties }));
};
/**
 * The triggerCustomPlanCardClickEvent function is used to track the card Click.
 * This will gather the custom properties and dispatch the segment event
 * @param action Get the target element of the event
 *
 */
const triggerCustomPlanCardClickEvent = (element) => {
  const cardElement = element.closest(paidCustomPlanCardSelector);
  const cardDataset = cardElement.dataset;
  const elementName = element.dataset.cardElement;
  const cardAreaClicked = segmentAreaClickedPerCardElement[elementName] || segmentAreaClickedPerCardElement.default;
  const cardImages = getCardImageUrls(cardElement);
  const position = parseInt(cardDataset.index, 10) + 1;

  const customProperties = {
    market_id: cardDataset.marketId,
    market_name: `${cardDataset.marketName}, ${cardDataset.stateAbbreviation}`,
    city: cardDataset.city,
    state: cardDataset.stateAbbreviation,
    zip: cardDataset.zip,
    builder_id: cardDataset.builderId,
    builder_name: cardDataset.brandName,
    community_id: cardDataset.communityId,
    community_name: cardDataset.communityName,
    primary_image_url: cardImages.primaryImage,
    plan_id: cardDataset.planId,
    home_type: cardDataset.homeStatus,
    home_price: cardDataset.price,
    position,
    area_clicked: cardAreaClicked,
    image_1_url: cardImages.primaryImage,
    is_paid: true,
    is_spotlight: !!cardDataset.wasMatchedAsSpotlightResult,
    community_type: cardDataset.communityType,
  };

  store.dispatch(segmentTrack({ eventName: 'Home Result Selected', customProperties }));
};

/**
 * The triggerBasicListingCardClickEvent function is used to track the card Click.
 * This will gather the custom properties and dispatch the segment event
 * @param action Get the target element of the event
 *
 */
const triggerBasicListingCardClickEvent = (element) => {
  const cardElement = element.closest(basicListingCardSelector);
  const cardDataset = cardElement.dataset;
  const elementName = element.dataset.cardElement;
  const cardAreaClicked = segmentAreaClickedPerCardElement[elementName] || segmentAreaClickedPerCardElement.default;
  const cardImages = getCardImageUrls(cardElement);
  const position = parseInt(cardDataset.index, 10) + 1;

  const customProperties = {
    market_id: cardDataset.marketId,
    market_name: `${cardDataset.marketName}, ${cardDataset.stateAbbreviation}`,
    city: cardDataset.city,
    state: cardDataset.stateAbbreviation,
    zip: cardDataset.zip,
    builder_id: cardDataset.builderId,
    builder_name: cardDataset.brandName,
    community_id: cardDataset.communityId,
    community_name: cardDataset.communityName,
    primary_image_url: cardImages.primaryImage,
    is_paid: false,
    id: cardDataset.id,
    home_type: cardDataset.homeStatus,
    home_price: cardDataset.price,
    position,
    area_clicked: cardAreaClicked,
    image_1_url: cardImages.primaryImage,
  };

  store.dispatch(segmentTrack({ eventName: 'Basic Detail Result Selected', customProperties }));
};

/**
 * The triggerBasicPlanCardClickEvent function is used to track the card Click.
 * This will gather the custom properties and dispatch the segment event
 * @param action Get the target element of the event
 *
 */
const triggerBasicPlanCardClickEvent = (element) => {
  const cardElement = element.closest(basicPlanCardSelector);
  const cardDataset = cardElement.dataset;
  const elementName = element.dataset.cardElement;
  const cardAreaClicked = segmentAreaClickedPerCardElement[elementName] || segmentAreaClickedPerCardElement.default;
  const cardImages = getCardImageUrls(cardElement);
  const position = parseInt(cardDataset.index, 10) + 1;

  const customProperties = {
    market_id: cardDataset.marketId,
    market_name: `${cardDataset.marketName}, ${cardDataset.stateAbbreviation}`,
    city: cardDataset.city,
    state: cardDataset.stateAbbreviation,
    zip: cardDataset.zip,
    builder_id: cardDataset.builderId,
    builder_name: cardDataset.brandName,
    community_id: cardDataset.communityId,
    community_name: cardDataset.communityName,
    primary_image_url: cardImages.primaryImage,
    plan_id: cardDataset.planId,
    plan_name: cardDataset.name,
    is_paid: false,
    home_type: cardDataset.homeStatus,
    home_price: cardDataset.price,
    position,
    area_clicked: cardAreaClicked,
    image_1_url: cardImages.primaryImage,
    is_spotlight: !!cardDataset.wasMatchedAsSpotlightResult,
    community_type: cardDataset.communityType,
  };

  store.dispatch(segmentTrack({ eventName: 'Home Result Selected', customProperties }));
};

/**
 * The triggerBasicSpecCardClickEvent function is used to track the card Click.
 * This will gather the custom properties and dispatch the segment event
 * @param action Get the target element of the event
 *
 */
const triggerBasicSpecCardClickEvent = (element) => {
  const cardElement = element.closest(basicSpecCardSelector);
  const cardDataset = cardElement.dataset;
  const elementName = element.dataset.cardElement;
  const cardAreaClicked = segmentAreaClickedPerCardElement[elementName] || segmentAreaClickedPerCardElement.default;
  const cardImages = getCardImageUrls(cardElement);
  const position = parseInt(cardDataset.index, 10) + 1;

  const customProperties = {
    market_id: cardDataset.marketId,
    market_name: `${cardDataset.marketName}, ${cardDataset.stateAbbreviation}`,
    city: cardDataset.city,
    state: cardDataset.stateAbbreviation,
    zip: cardDataset.zip,
    builder_id: cardDataset.builderId,
    builder_name: cardDataset.brandName,
    community_id: cardDataset.communityId,
    community_name: cardDataset.communityName,
    primary_image_url: cardImages.primaryImage,
    spec_id: cardDataset.specId,
    spec_name: cardDataset.name,
    plan_id: cardDataset.planId,
    is_paid: false,
    home_type: cardDataset.homeStatus,
    home_price: cardDataset.price,
    position,
    area_clicked: cardAreaClicked,
    image_1_url: cardImages.primaryImage,
    is_spotlight: !!cardDataset.wasMatchedAsSpotlightResult,
    community_type: cardDataset.communityType,
  };

  store.dispatch(segmentTrack({ eventName: 'Home Result Selected', customProperties }));
};

paidPlanCardClicked$.subscribe(triggerPlanCardClickEvent);
paidSpecCardClicked$.subscribe(triggerSpecCardClickEvent);
manufacturedPlanCardClicked$.subscribe(triggerManufacturedPlanCardClickEvent);
manufacturedSpecCardClicked$.subscribe(triggerManufacturedSpecCardClickEvent);
paidCustomPlanCardClicked$.subscribe(triggerCustomPlanCardClickEvent);
basicListingCardClicked$.subscribe(triggerBasicListingCardClickEvent);
basicPlanCardClicked$.subscribe(triggerBasicPlanCardClickEvent);
basicSpecCardClicked$.subscribe(triggerBasicSpecCardClickEvent);

// observable created when there is a click in the list view.
// It will filter the actions to identify the ones performed in the card which takes the user to detail page.
// filters the clicks on lead cta and fav button.
const customAndBuildOnYourLotHomeCardClicked$ = listViewSectionClicks$.pipe(
  map((event) => {
    const foundSelector = customAndBuildOnYourLotHomesSelectors.find((selector) => event.target.closest(selector));
return { event, foundSelector };
  }),
  filter(({ foundSelector }) => !!foundSelector),
  filter(({ event }) => !event.target.dataset?.leadGeneratorOpener),
  filter(({ event }) => !event.target.closest(cardfavoriteButtonSelector)),
  map(({ event, foundSelector }) => ({ event, foundSelector }))
);

/**
 * The triggerCommunityCardClickEvent function is used to track the card Click.
 * This will gather the custom properties and dispatch the segment event
 * @param action Get the target element of the event
 *
 */
const triggerCustomAndBuildOnYourLotHomeCardClickEvent = ({ event, foundSelector }) => {
  const cardElement = event.target.closest(foundSelector);
  const cardDataset = cardElement.dataset;
  const elementName = event.target.dataset.cardElement;
  const cardAreaClicked = segmentAreaClickedPerCardElement[elementName] || segmentAreaClickedPerCardElement.default;
  const cardImages = getCardImageUrls(cardElement);
  const position = parseInt(cardDataset.index, 10) + 1;

  const customProperties = {
    market_id: cardDataset.marketId,
    market_name: `${cardDataset.marketName}, ${cardDataset.stateAbbreviation}`,
    city: cardDataset.city,
    state: cardDataset.stateAbbreviation,
    zip: cardDataset.zip,
    builder_id: cardDataset.builderId,
    builder_name: cardDataset.brandName,
    community_id: cardDataset.communityId,
    community_name: cardDataset.communityName,
    primary_image_url: cardImages.primaryImage,
    plan_id: cardDataset.planId,
    spec_id: cardDataset.specId,
    home_type: cardDataset.homeStatus,
    home_price: cardDataset.price,
    position,
    area_clicked: cardAreaClicked,
    image_1_url: cardImages.primaryImage,
    is_paid: cardDataset.isBasic !== 'true',
    is_spotlight: !!cardDataset.wasMatchedAsSpotlightResult,
    community_type: cardDataset.communityType,
  };

  store.dispatch(segmentTrack({ eventName: 'Home Result Selected', customProperties }));
};

customAndBuildOnYourLotHomeCardClicked$.subscribe(triggerCustomAndBuildOnYourLotHomeCardClickEvent);


// observable created when there is a click in the list view.
// It will filter the actions to identify the ones performed in the card which takes the user to detail page.
// filters the clicks on lead cta and fav button.
const customAndBuildOnYourLotCommunityCardClicked$ = listViewSectionClicks$.pipe(
  map((event) => {
    const foundSelector = customAndBuildOnYourLotCommunitiesSelectors
      .find((selector) => event.target.closest(selector));
    return { event, foundSelector };
  }),
  filter(({ foundSelector }) => !!foundSelector),
  filter(({ event }) => !event.target.dataset?.leadGeneratorOpener),
  filter(({ event }) => !event.target.closest(cardfavoriteButtonSelector)),
  map(({ event, foundSelector }) => ({ event, foundSelector }))
);

/**
 * The triggerCommunityCardClickEvent function is used to track the card Click.
 * This will gather the custom properties and dispatch the segment event
 * @param action Get the target element of the event
 *
 */
const triggerCustomAndBuildOnYourLotCommunityCardClickEvent = ({ event, foundSelector }) => {
  const cardElement = event.target.closest(foundSelector);
  const cardDataset = cardElement.dataset;
  const elementName = event.target.dataset.cardElement;
  const cardAreaClicked = segmentAreaClickedPerCardElement[elementName] || segmentAreaClickedPerCardElement.default;
  const cardImages = getCardImageUrls(cardElement);
  const position = parseInt(cardDataset.index, 10) + 1;

  const customProperties = {
    market_id: cardDataset.marketId,
    market_name: `${cardDataset.marketName}, ${cardDataset.stateAbbreviation}`,
    city: cardDataset.city,
    state: cardDataset.stateAbbreviation,
    zip: cardDataset.zip,
    builder_id: cardDataset.builderId,
    builder_name: cardDataset.brandName,
    community_id: cardDataset.id,
    community_name: cardDataset.communityName,
    community_status: cardDataset.marketingStatusLabel,
    primary_image_url: cardDataset.imageUrl,
    community_low_price: cardDataset.priceLow,
    community_high_price: cardDataset.priceHigh,
    position,
    area_clicked: cardAreaClicked,
    image_1_url: cardImages.primaryImage,
    image_2_url: cardImages.secondaryImage,
    image_3_url: cardImages.terciaryImage,
    is_paid: cardDataset.isBasic !== 'true',
    is_spotlight: !!cardDataset.wasMatchedAsSpotlightResult,
    community_type: cardDataset.communityType,
  };

  store.dispatch(segmentTrack({ eventName: 'Community Result Selected', customProperties }));
};

customAndBuildOnYourLotCommunityCardClicked$.subscribe(triggerCustomAndBuildOnYourLotCommunityCardClickEvent);

// observable created when there is a click in the list view.
// It will filter the actions to identify the ones performed in the card which takes the user to detail page.
// filters the clicks on lead cta and fav button.
const manufacturedCommunityCardClicked$ = listViewSectionClicks$.pipe(
  filter((event) => event.target.closest(paidManufacturedCommunityCardSelector) ||
    event.target.closest(basicManufacturedCommunityCardSelector)),
  filter((event) => !event.target.dataset?.leadGeneratorOpener),
  filter((event) => !event.target.closest(cardfavoriteButtonSelector)),
  map((event) => event.target)
);

/**
 * The triggerCommunityCardClickEvent function is used to track the card Click.
 * This will gather the custom properties and dispatch the segment event
 * @param action Get the target element of the event
 *
 */
const triggerManufacturedCommunityCardClickEvent = (element) => {
  const cardElement = element.closest(paidManufacturedCommunityCardSelector) ||
    element.closest(basicManufacturedCommunityCardSelector);
  const cardDataset = cardElement.dataset;
  const elementName = element.dataset.cardElement;
  const cardAreaClicked = segmentAreaClickedPerCardElement[elementName] || segmentAreaClickedPerCardElement.default;
  const cardImages = getCardImageUrls(cardElement);
  const position = parseInt(cardDataset.index, 10) + 1;

  const customProperties = {
    market_id: cardDataset.marketId,
    market_name: `${cardDataset.marketName}, ${cardDataset.stateAbbreviation}`,
    city: cardDataset.city,
    state: cardDataset.stateAbbreviation,
    zip: cardDataset.zip,
    builder_id: cardDataset.builderId,
    builder_name: cardDataset.brandName,
    community_id: cardDataset.id,
    community_name: cardDataset.communityName,
    community_status: cardDataset.marketingStatusLabel,
    primary_image_url: cardDataset.imageUrl,
    community_low_price: cardDataset.priceLow,
    community_high_price: cardDataset.priceHigh,
    position,
    area_clicked: cardAreaClicked,
    image_1_url: cardImages.primaryImage,
    image_2_url: cardImages.secondaryImage,
    image_3_url: cardImages.terciaryImage,
    is_paid: cardDataset.isBasic !== 'true',
    is_spotlight: !!cardDataset.wasMatchedAsSpotlightResult,
    community_type: cardDataset.communityType,
  };

  store.dispatch(segmentTrack({ eventName: 'Community Result Selected', customProperties }));
};

manufacturedCommunityCardClicked$.subscribe(triggerManufacturedCommunityCardClickEvent);
// ! Home Favorited Event /

// Identifies if there is a new saved object of a given type and maps it.
const filterFavoritedByType = (payload, type) => {
  const [lastValue, newValue] = payload;

  // this filter, validates if it was an item addition to the existing array, and works with the addedd item
  const newFavoritedItems = newValue.filter((item) => !lastValue.includes(item));
  if (!newFavoritedItems || newFavoritedItems.length === 0) {
    return null;
  }

  if (newFavoritedItems[0].type !== type) {
    return null;
  }

  return newFavoritedItems[0];
};

// observable created when there is an add to the redux store object "pendingSaves".
// this object contains a list of items that the user clicked but are not already saved.
// It will filter the actions to now when it changes.
const onPlanPendingFavoritesChanged$ = store$.pipe(
  map(selectPendingSaves),
  filter(Boolean),
  distinctUntilChanged(),
  pairwise(),
  map((payload) => filterFavoritedByType(payload, 'HomePlan')),
  filter(Boolean)
);

/**
 * The triggerPlanFavoritedEvent function is used to track the favorited event.
 * which happens when the user clicks in the fav button to save it.
 * This will gather the custom properties and dispatch the segment event
 * @param action Get the target element of the event
 *
 */
const triggerPlanFavoritedEvent = (payload) => {
  const itemId = payload.listingInformation.id;
  const cardElement = document.querySelector(`[data-paid-plan][data-id="${itemId}"]`);

  if (!cardElement) {
    return;
  }
  const cardDataset = cardElement.dataset;
  const primaryImageUrl =
    cardDataset.imageUrl !== '' ? cardDataset.imageUrl : getCardImageUrls(cardElement).primaryImage;

  const customProperties = {
    market_id: cardDataset.marketId,
    market_name: `${cardDataset.marketName}, ${cardDataset.stateAbbreviation}`,
    city: cardDataset.city,
    state: cardDataset.stateAbbreviation,
    zip: cardDataset.zip,
    builder_id: cardDataset.builderId,
    builder_name: cardDataset.brandName,
    community_id: cardDataset.communityId,
    community_name: cardDataset.communityName,
    primary_image_url: primaryImageUrl,
    plan_id: cardDataset.planId,
    home_type: cardDataset.homeStatus,
    home_price: cardDataset.price,
    is_paid: true,
    is_spotlight: !!cardDataset.wasMatchedAsSpotlightResult,
    community_type: cardDataset.communityType,
    is_neighborhood: isNeighborhoodPage(),
    is_schooldistrict: isSchoolDistrictPage(),
  };

  store.dispatch(segmentTrack({ eventName: 'Home Favorited', customProperties }));
};

onPlanPendingFavoritesChanged$.subscribe(triggerPlanFavoritedEvent);

// observable created when there is an add to the redux store object "pendingSaves".
// this object contains a list of items that the user clicked but are not already saved.
// It will filter the actions to now when it changes.
const onSpecPendingFavoritesChanged$ = store$.pipe(
  map(selectPendingSaves),
  filter(Boolean),
  distinctUntilChanged(),
  pairwise(),
  map((payload) => filterFavoritedByType(payload, 'HomeSpec')),
  filter(Boolean)
);

/**
 * The triggerSpecFavoritedEvent function is used to track the favorited event.
 * which happens when the user clicks in the fav button to save it.
 * This will gather the custom properties and dispatch the segment event
 * @param action Get the target element of the event
 *
 */
const triggerSpecFavoritedEvent = (payload) => {
  const itemId = payload.listingInformation.id;
  const cardElement = document.querySelector(`[data-paid-spec][data-id="${itemId}"]`);

  if (!cardElement) {
    return;
  }

  const cardDataset = cardElement.dataset;
  const primaryImageUrl =
    cardDataset.imageUrl !== '' ? cardDataset.imageUrl : getCardImageUrls(cardElement).primaryImage;

  const customProperties = {
    market_id: cardDataset.marketId,
    market_name: `${cardDataset.marketName}, ${cardDataset.stateAbbreviation}`,
    city: cardDataset.city,
    state: cardDataset.stateAbbreviation,
    zip: cardDataset.zip,
    builder_id: cardDataset.builderId,
    builder_name: cardDataset.brandName,
    community_id: cardDataset.communityId,
    community_name: cardDataset.communityName,
    primary_image_url: primaryImageUrl,
    spec_id: cardDataset.specId,
    home_type: cardDataset.homeStatus,
    home_price: cardDataset.price,
    is_paid: true,
    is_spotlight: !!cardDataset.wasMatchedAsSpotlightResult,
    community_type: cardDataset.communityType,
    is_neighborhood: isNeighborhoodPage(),
    is_schooldistrict: isSchoolDistrictPage(),
  };

  store.dispatch(segmentTrack({ eventName: 'Home Favorited', customProperties }));
};

onSpecPendingFavoritesChanged$.subscribe(triggerSpecFavoritedEvent);

// observable created when there is an add to the redux store object "pendingSaves".
// this object contains a list of items that the user clicked but are not already saved.
// It will filter the actions to now when it changes.
const onBasicPlanPendingFavoritesChanged$ = pendingListingSavesFavorites$.pipe(
  map((payload) => filterFavoritedByType(payload, FavoriteType.Plan.toString())),
  filter(Boolean)
);

/**
 * The triggerPlanFavoritedEvent function is used to track the favorited event.
 * which happens when the user clicks in the fav button to save it.
 * This will gather the custom properties and dispatch the segment event
 * @param action Get the target element of the event
 *
 */
const triggerBasicPlanFavoritedEvent = (payload) => {
  const itemId = payload.listingInformation.id;
  const cardElement = document.querySelector(`[data-basic-plan][data-id="${itemId}"]`);

  if (!cardElement) {
    return;
  }
  const cardDataset = cardElement.dataset;
  const primaryImageUrl =
    cardDataset.imageUrl !== '' ? cardDataset.imageUrl : getCardImageUrls(cardElement).primaryImage;

  const customProperties = {
    market_id: cardDataset.marketId,
    market_name: `${cardDataset.marketName}, ${cardDataset.stateAbbreviation}`,
    city: cardDataset.city,
    state: cardDataset.stateAbbreviation,
    zip: cardDataset.zip,
    builder_id: cardDataset.builderId,
    builder_name: cardDataset.brandName,
    community_id: cardDataset.communityId,
    community_name: cardDataset.communityName,
    primary_image_url: primaryImageUrl,
    plan_id: cardDataset.planId,
    home_type: cardDataset.homeStatus,
    home_price: cardDataset.price,
    is_paid: false,
    is_spotlight: !!cardDataset.wasMatchedAsSpotlightResult,
    community_type: cardDataset.communityType,
    is_neighborhood: isNeighborhoodPage(),
    is_schooldistrict: isSchoolDistrictPage(),
  };

  store.dispatch(segmentTrack({ eventName: 'Home Favorited', customProperties }));
};

onBasicPlanPendingFavoritesChanged$.subscribe(triggerBasicPlanFavoritedEvent);

// observable created when there is an add to the redux store object "pendingSaves".
// this object contains a list of items that the user clicked but are not already saved.
// It will filter the actions to now when it changes.
const onBasicSpecPendingFavoritesChanged$ = pendingListingSavesFavorites$.pipe(
  map((payload) => filterFavoritedByType(payload, FavoriteType.Spec.toString())),
  filter(Boolean)
);

/**
 * The triggerSpecFavoritedEvent function is used to track the favorited event.
 * which happens when the user clicks in the fav button to save it.
 * This will gather the custom properties and dispatch the segment event
 * @param action Get the target element of the event
 *
 */
const triggerBasicSpecFavoritedEvent = (payload) => {
  const itemId = payload.listingInformation.id;
  const cardElement = document.querySelector(`[data-basic-spec][data-id="${itemId}"]`);

  if (!cardElement) {
    return;
  }

  const cardDataset = cardElement.dataset;
  const primaryImageUrl =
    cardDataset.imageUrl !== '' ? cardDataset.imageUrl : getCardImageUrls(cardElement).primaryImage;

  const customProperties = {
    market_id: cardDataset.marketId,
    market_name: `${cardDataset.marketName}, ${cardDataset.stateAbbreviation}`,
    city: cardDataset.city,
    state: cardDataset.stateAbbreviation,
    zip: cardDataset.zip,
    builder_id: cardDataset.builderId,
    builder_name: cardDataset.brandName,
    community_id: cardDataset.communityId,
    community_name: cardDataset.communityName,
    primary_image_url: primaryImageUrl,
    spec_id: cardDataset.specId,
    home_type: cardDataset.homeStatus,
    home_price: cardDataset.price,
    is_paid: false,
    is_spotlight: !!cardDataset.wasMatchedAsSpotlightResult,
    community_type: cardDataset.communityType,
    is_neighborhood: isNeighborhoodPage(),
    is_schooldistrict: isSchoolDistrictPage(),
  };

  store.dispatch(segmentTrack({ eventName: 'Home Favorited', customProperties }));
};

onBasicSpecPendingFavoritesChanged$.subscribe(triggerBasicSpecFavoritedEvent);

// observable created when there is an add to the redux store object "pendingSaves".
// this object contains a list of items that the user clicked but are not already saved.
// It will filter the actions to now when it changes.
const onPaidBuildOnYourLotPlanPendingFavoritesChanged$ = pendingListingSavesFavorites$.pipe(
  map((payload) => filterFavoritedByType(payload, FavoriteType.Plan.toString())),
  filter(Boolean)
);

/**
 * The triggerPaidBOYLPlanFavoritedEvent function is used to track the favorited event.
 * which happens when the user clicks in the fav button to save it.
 * This will gather the custom properties and dispatch the segment event
 * @param action Get the target element of the event
 *
 */
const triggerPaidBuildOnYourLotPlanFavoritedEvent = (payload) => {
  const itemId = payload.listingInformation.id;
  const cardElement = document.querySelector(`[data-paid-boyl-plan][data-id="${itemId}"]`);

  if (!cardElement) {
    return;
  }
  const cardDataset = cardElement.dataset;
  const primaryImageUrl =
    cardDataset.imageUrl !== '' ? cardDataset.imageUrl : getCardImageUrls(cardElement).primaryImage;

  const customProperties = {
    market_id: cardDataset.marketId,
    market_name: `${cardDataset.marketName}, ${cardDataset.stateAbbreviation}`,
    city: cardDataset.city,
    state: cardDataset.stateAbbreviation,
    zip: cardDataset.zip,
    builder_id: cardDataset.builderId,
    builder_name: cardDataset.brandName,
    community_id: cardDataset.communityId,
    community_name: cardDataset.communityName,
    primary_image_url: primaryImageUrl,
    plan_id: cardDataset.planId,
    is_paid: true,
    home_type: cardDataset.homeStatus,
    home_price: cardDataset.price,
    is_spotlight: !!cardDataset.wasMatchedAsSpotlightResult,
    community_type: cardDataset.communityType,
    is_neighborhood: isNeighborhoodPage(),
    is_schooldistrict: isSchoolDistrictPage(),
  };

  store.dispatch(segmentTrack({ eventName: 'Home Favorited', customProperties }));
};

onPaidBuildOnYourLotPlanPendingFavoritesChanged$.subscribe(triggerPaidBuildOnYourLotPlanFavoritedEvent);

// observable created when there is an add to the redux store object "pendingSaves".
// this object contains a list of items that the user clicked but are not already saved.
// It will filter the actions to now when it changes.
const onPaidBuildOnYourLotSpecPendingFavoritesChanged$ = pendingListingSavesFavorites$.pipe(
  map((payload) => filterFavoritedByType(payload, FavoriteType.Spec.toString())),
  filter(Boolean)
);

/**
 * The triggerPaidBOYLSpecFavoritedEvent function is used to track the favorited event.
 * which happens when the user clicks in the fav button to save it.
 * This will gather the custom properties and dispatch the segment event
 * @param action Get the target element of the event
 *
 */
const triggerPaidBuildOnYourLotSpecFavoritedEvent = (payload) => {
  const itemId = payload.listingInformation.id;
  const cardElement = document.querySelector(`[data-paid-boyl-spec][data-id="${itemId}"]`);

  if (!cardElement) {
    return;
  }
  const cardDataset = cardElement.dataset;
  const primaryImageUrl =
    cardDataset.imageUrl !== '' ? cardDataset.imageUrl : getCardImageUrls(cardElement).primaryImage;

  const customProperties = {
    market_id: cardDataset.marketId,
    market_name: `${cardDataset.marketName}, ${cardDataset.stateAbbreviation}`,
    city: cardDataset.city,
    state: cardDataset.stateAbbreviation,
    zip: cardDataset.zip,
    builder_id: cardDataset.builderId,
    builder_name: cardDataset.brandName,
    community_id: cardDataset.communityId,
    community_name: cardDataset.communityName,
    primary_image_url: primaryImageUrl,
    plan_id: cardDataset.planId,
    spec_id: cardDataset.specId,
    is_paid: true,
    home_type: cardDataset.homeStatus,
    home_price: cardDataset.price,
    is_spotlight: !!cardDataset.wasMatchedAsSpotlightResult,
    community_type: cardDataset.communityType,
    is_neighborhood: isNeighborhoodPage(),
    is_schooldistrict: isSchoolDistrictPage(),
  };

  store.dispatch(segmentTrack({ eventName: 'Home Favorited', customProperties }));
};

onPaidBuildOnYourLotSpecPendingFavoritesChanged$.subscribe(triggerPaidBuildOnYourLotSpecFavoritedEvent);

// observable created when there is an add to the redux store object "pendingSaves".
// this object contains a list of items that the user clicked but are not already saved.
// It will filter the actions to now when it changes.
const onPaidCustomHomePlanPendingFavoritesChanged$ = pendingListingSavesFavorites$.pipe(
  map((payload) => filterFavoritedByType(payload, FavoriteType.Plan.toString())),
  filter(Boolean)
);

/**
 * The triggerPaidCustomHomePlanFavoritedEvent function is used to track the favorited event.
 * which happens when the user clicks in the fav button to save it.
 * This will gather the custom properties and dispatch the segment event
 * @param action Get the target element of the event
 *
 */
const triggerPaidCustomHomePlanFavoritedEvent = (payload) => {
  const itemId = payload.listingInformation.id;
  const cardElement = document.querySelector(`[data-paid-custom-plan][data-id="${itemId}"]`);

  if (!cardElement) {
    return;
  }
  const cardDataset = cardElement.dataset;
  const primaryImageUrl =
    cardDataset.imageUrl !== '' ? cardDataset.imageUrl : getCardImageUrls(cardElement).primaryImage;

  const customProperties = {
    market_id: cardDataset.marketId,
    market_name: `${cardDataset.marketName}, ${cardDataset.stateAbbreviation}`,
    city: cardDataset.city,
    state: cardDataset.stateAbbreviation,
    zip: cardDataset.zip,
    builder_id: cardDataset.builderId,
    builder_name: cardDataset.brandName,
    community_id: cardDataset.communityId,
    community_name: cardDataset.communityName,
    primary_image_url: primaryImageUrl,
    plan_id: cardDataset.planId,
    is_paid: true,
    home_type: cardDataset.homeStatus,
    home_price: cardDataset.price,
    is_spotlight: !!cardDataset.wasMatchedAsSpotlightResult,
    community_type: cardDataset.communityType,
    is_neighborhood: isNeighborhoodPage(),
    is_schooldistrict: isSchoolDistrictPage(),
  };

  store.dispatch(segmentTrack({ eventName: 'Home Favorited', customProperties }));
};

onPaidCustomHomePlanPendingFavoritesChanged$.subscribe(triggerPaidCustomHomePlanFavoritedEvent);

// observable created when there is an add to the redux store object "pendingSaves".
// this object contains a list of items that the user clicked but are not already saved.
// It will filter the actions to now when it changes.
const onPaidCustomHomeSpecPendingFavoritesChanged$ = pendingListingSavesFavorites$.pipe(
  map((payload) => filterFavoritedByType(payload, FavoriteType.Spec.toString())),
  filter(Boolean)
);

/**
 * The triggerPaidCustomHomeSpecFavoritedEvent function is used to track the favorited event.
 * which happens when the user clicks in the fav button to save it.
 * This will gather the custom properties and dispatch the segment event
 * @param action Get the target element of the event
 *
 */
const triggerPaidCustomHomeSpecFavoritedEvent = (payload) => {
  const itemId = payload.listingInformation.id;
  const cardElement = document.querySelector(`[data-paid-custom-spec][data-id="${itemId}"]`);

  if (!cardElement) {
    return;
  }
  const cardDataset = cardElement.dataset;
  const primaryImageUrl =
    cardDataset.imageUrl !== '' ? cardDataset.imageUrl : getCardImageUrls(cardElement).primaryImage;

  const customProperties = {
    market_id: cardDataset.marketId,
    market_name: `${cardDataset.marketName}, ${cardDataset.stateAbbreviation}`,
    city: cardDataset.city,
    state: cardDataset.stateAbbreviation,
    zip: cardDataset.zip,
    builder_id: cardDataset.builderId,
    builder_name: cardDataset.brandName,
    community_id: cardDataset.communityId,
    community_name: cardDataset.communityName,
    primary_image_url: primaryImageUrl,
    plan_id: cardDataset.planId,
    spec_id: cardDataset.specId,
    is_paid: true,
    home_type: cardDataset.homeStatus,
    home_price: cardDataset.price,
    is_spotlight: !!cardDataset.wasMatchedAsSpotlightResult,
    community_type: cardDataset.communityType,
    is_neighborhood: isNeighborhoodPage(),
    is_schooldistrict: isSchoolDistrictPage(),
  };

  store.dispatch(segmentTrack({ eventName: 'Home Favorited', customProperties }));
};

onPaidCustomHomeSpecPendingFavoritesChanged$.subscribe(triggerPaidCustomHomeSpecFavoritedEvent);

// observable created when there is an add to the redux store object "pendingSaves".
// this object contains a list of items that the user clicked but are not already saved.
// It will filter the actions to now when it changes.
const onBasicBuildOnYourLotPlanPendingFavoritesChanged$ = pendingListingSavesFavorites$.pipe(
  map((payload) => filterFavoritedByType(payload, FavoriteType.Plan.toString())),
  filter(Boolean)
);

/**
 * The triggerPaidBOYLPlanFavoritedEvent function is used to track the favorited event.
 * which happens when the user clicks in the fav button to save it.
 * This will gather the custom properties and dispatch the segment event
 * @param action Get the target element of the event
 *
 */
const triggerBasicBuildOnYourLotPlanFavoritedEvent = (payload) => {
  const itemId = payload.listingInformation.id;
  const cardElement = document.querySelector(`[data-basic-boyl-plan][data-id="${itemId}"]`);

  if (!cardElement) {
    return;
  }
  const cardDataset = cardElement.dataset;
  const primaryImageUrl =
    cardDataset.imageUrl !== '' ? cardDataset.imageUrl : getCardImageUrls(cardElement).primaryImage;

  const customProperties = {
    market_id: cardDataset.marketId,
    market_name: `${cardDataset.marketName}, ${cardDataset.stateAbbreviation}`,
    city: cardDataset.city,
    state: cardDataset.stateAbbreviation,
    zip: cardDataset.zip,
    builder_id: cardDataset.builderId,
    builder_name: cardDataset.brandName,
    community_id: cardDataset.communityId,
    community_name: cardDataset.communityName,
    primary_image_url: primaryImageUrl,
    plan_id: cardDataset.planId,
    is_paid: false,
    home_type: cardDataset.homeStatus,
    home_price: cardDataset.price,
    is_spotlight: !!cardDataset.wasMatchedAsSpotlightResult,
    community_type: cardDataset.communityType,
    is_neighborhood: isNeighborhoodPage(),
    is_schooldistrict: isSchoolDistrictPage(),
  };

  store.dispatch(segmentTrack({ eventName: 'Home Favorited', customProperties }));
};

onBasicBuildOnYourLotPlanPendingFavoritesChanged$.subscribe(triggerBasicBuildOnYourLotPlanFavoritedEvent);

// observable created when there is an add to the redux store object "pendingSaves".
// this object contains a list of items that the user clicked but are not already saved.
// It will filter the actions to now when it changes.
const onBasicBuildOnYourLotSpecPendingFavoritesChanged$ = pendingListingSavesFavorites$.pipe(
  map((payload) => filterFavoritedByType(payload, FavoriteType.Spec.toString())),
  filter(Boolean)
);

/**
 * The triggerPaidBOYLSpecFavoritedEvent function is used to track the favorited event.
 * which happens when the user clicks in the fav button to save it.
 * This will gather the custom properties and dispatch the segment event
 * @param action Get the target element of the event
 *
 */
const triggerBasicBuildOnYourLotSpecFavoritedEvent = (payload) => {
  const itemId = payload.listingInformation.id;
  const cardElement = document.querySelector(`[data-basic-boyl-spec][data-id="${itemId}"]`);

  if (!cardElement) {
    return;
  }
  const cardDataset = cardElement.dataset;
  const primaryImageUrl =
    cardDataset.imageUrl !== '' ? cardDataset.imageUrl : getCardImageUrls(cardElement).primaryImage;

  const customProperties = {
    market_id: cardDataset.marketId,
    market_name: `${cardDataset.marketName}, ${cardDataset.stateAbbreviation}`,
    city: cardDataset.city,
    state: cardDataset.stateAbbreviation,
    zip: cardDataset.zip,
    builder_id: cardDataset.builderId,
    builder_name: cardDataset.brandName,
    community_id: cardDataset.communityId,
    community_name: cardDataset.communityName,
    primary_image_url: primaryImageUrl,
    plan_id: cardDataset.planId,
    spec_id: cardDataset.specId,
    is_paid: false,
    home_type: cardDataset.homeStatus,
    home_price: cardDataset.price,
    is_spotlight: !!cardDataset.wasMatchedAsSpotlightResult,
    community_type: cardDataset.communityType,
    is_neighborhood: isNeighborhoodPage(),
    is_schooldistrict: isSchoolDistrictPage(),
  };

  store.dispatch(segmentTrack({ eventName: 'Home Favorited', customProperties }));
};

onBasicBuildOnYourLotSpecPendingFavoritesChanged$.subscribe(triggerBasicBuildOnYourLotSpecFavoritedEvent);

// observable created when there is an add to the redux store object "pendingSaves".
// this object contains a list of items that the user clicked but are not already saved.
// It will filter the actions to now when it changes.
const onBasicCustomHomePlanPendingFavoritesChanged$ = pendingListingSavesFavorites$.pipe(
  map((payload) => filterFavoritedByType(payload, FavoriteType.Plan.toString())),
  filter(Boolean)
);

/**
 * The triggerPaidCustomHomePlanFavoritedEvent function is used to track the favorited event.
 * which happens when the user clicks in the fav button to save it.
 * This will gather the custom properties and dispatch the segment event
 * @param action Get the target element of the event
 *
 */
const triggerBasicCustomHomePlanFavoritedEvent = (payload) => {
  const itemId = payload.listingInformation.id;
  const cardElement = document.querySelector(`[data-basic-custom-plan][data-id="${itemId}"]`);

  if (!cardElement) {
    return;
  }
  const cardDataset = cardElement.dataset;
  const primaryImageUrl =
    cardDataset.imageUrl !== '' ? cardDataset.imageUrl : getCardImageUrls(cardElement).primaryImage;

  const customProperties = {
    market_id: cardDataset.marketId,
    market_name: `${cardDataset.marketName}, ${cardDataset.stateAbbreviation}`,
    city: cardDataset.city,
    state: cardDataset.stateAbbreviation,
    zip: cardDataset.zip,
    builder_id: cardDataset.builderId,
    builder_name: cardDataset.brandName,
    community_id: cardDataset.communityId,
    community_name: cardDataset.communityName,
    primary_image_url: primaryImageUrl,
    plan_id: cardDataset.planId,
    is_paid: false,
    home_type: cardDataset.homeStatus,
    home_price: cardDataset.price,
    is_spotlight: !!cardDataset.wasMatchedAsSpotlightResult,
    community_type: cardDataset.communityType,
    is_neighborhood: isNeighborhoodPage(),
    is_schooldistrict: isSchoolDistrictPage(),
  };

  store.dispatch(segmentTrack({ eventName: 'Home Favorited', customProperties }));
};

onBasicCustomHomePlanPendingFavoritesChanged$.subscribe(triggerBasicCustomHomePlanFavoritedEvent);

// observable created when there is an add to the redux store object "pendingSaves".
// this object contains a list of items that the user clicked but are not already saved.
// It will filter the actions to now when it changes.
const onBasicCustomHomeSpecPendingFavoritesChanged$ = pendingListingSavesFavorites$.pipe(
  map((payload) => filterFavoritedByType(payload, FavoriteType.Spec.toString())),
  filter(Boolean)
);

/**
 * The triggerPaidCustomHomeSpecFavoritedEvent function is used to track the favorited event.
 * which happens when the user clicks in the fav button to save it.
 * This will gather the custom properties and dispatch the segment event
 * @param action Get the target element of the event
 *
 */
const triggerBasicCustomHomeSpecFavoritedEvent = (payload) => {
  const itemId = payload.listingInformation.id;
  const cardElement = document.querySelector(`[data-basic-custom-spec][data-id="${itemId}"]`);

  if (!cardElement) {
    return;
  }
  const cardDataset = cardElement.dataset;
  const primaryImageUrl =
    cardDataset.imageUrl !== '' ? cardDataset.imageUrl : getCardImageUrls(cardElement).primaryImage;

  const customProperties = {
    market_id: cardDataset.marketId,
    market_name: `${cardDataset.marketName}, ${cardDataset.stateAbbreviation}`,
    city: cardDataset.city,
    state: cardDataset.stateAbbreviation,
    zip: cardDataset.zip,
    builder_id: cardDataset.builderId,
    builder_name: cardDataset.brandName,
    community_id: cardDataset.communityId,
    community_name: cardDataset.communityName,
    primary_image_url: primaryImageUrl,
    plan_id: cardDataset.planId,
    spec_id: cardDataset.specId,
    is_paid: false,
    home_type: cardDataset.homeStatus,
    home_price: cardDataset.price,
    is_spotlight: !!cardDataset.wasMatchedAsSpotlightResult,
    community_type: cardDataset.communityType,
    is_neighborhood: isNeighborhoodPage(),
    is_schooldistrict: isSchoolDistrictPage(),
  };

  store.dispatch(segmentTrack({ eventName: 'Home Favorited', customProperties }));
};

onBasicCustomHomeSpecPendingFavoritesChanged$.subscribe(triggerBasicCustomHomeSpecFavoritedEvent);

// observable created when there is an add to the redux store object "pendingSaves".
// this object contains a list of items that the user clicked but are not already saved.
// It will filter the actions to now when it changes.
const manufacturedHomeSpecPendingFavoritesChanged$ = pendingListingSavesFavorites$.pipe(
  map((payload) => filterFavoritedByType(payload, FavoriteType.Spec.toString())),
  filter(Boolean)
);
const manufacturedHomePlanPendingFavoritesChanged$ = pendingListingSavesFavorites$.pipe(
  map((payload) => filterFavoritedByType(payload, FavoriteType.Plan.toString())),
  filter(Boolean)
);

/**
 * The triggerPaidCustomHomeSpecFavoritedEvent function is used to track the favorited event.
 * which happens when the user clicks in the fav button to save it.
 * This will gather the custom properties and dispatch the segment event
 * @param action Get the target element of the event
 *
 */
const triggerManufacturedHomePlanFavoritedEvent = (payload) => {
  const itemId = payload.listingInformation.id;
  const itemType = payload.listingInformation.isBasic !== 'true'
    ? 'paid' : 'basic';

  const cardElement = document.querySelector(`[data-${itemType}-manufactured-plan][data-id="${itemId}"]`);

  if (!cardElement) {
    return;
  }
  const cardDataset = cardElement.dataset;
  const primaryImageUrl =
    cardDataset.imageUrl !== '' ? cardDataset.imageUrl : getCardImageUrls(cardElement).primaryImage;

  const customProperties = {
    market_id: cardDataset.marketId,
    market_name: `${cardDataset.marketName}, ${cardDataset.stateAbbreviation}`,
    city: cardDataset.city,
    state: cardDataset.stateAbbreviation,
    zip: cardDataset.zip,
    builder_id: cardDataset.builderId,
    builder_name: cardDataset.brandName,
    community_id: cardDataset.communityId,
    community_name: cardDataset.communityName,
    primary_image_url: primaryImageUrl,
    plan_id: cardDataset.planId,
    is_paid: cardDataset.isBasic !== 'true',
    home_type: cardDataset.homeStatus,
    home_price: cardDataset.price,
    is_spotlight: !!cardDataset.wasMatchedAsSpotlightResult,
    community_type: cardDataset.communityType,
    is_neighborhood: isNeighborhoodPage(),
    is_schooldistrict: isSchoolDistrictPage(),
  };

  store.dispatch(segmentTrack({ eventName: 'Home Favorited', customProperties }));
};

const triggerManufacturedHomeSpecFavoritedEvent = (payload) => {
  const itemId = payload.listingInformation.id;
  const itemType = payload.listingInformation.isBasic !== 'true'
    ? 'paid' : 'basic';

  const cardElement = document.querySelector(`[data-${itemType}-manufactured-spec][data-id="${itemId}"]`);

  if (!cardElement) {
    return;
  }
  const cardDataset = cardElement.dataset;
  const primaryImageUrl =
    cardDataset.imageUrl !== '' ? cardDataset.imageUrl : getCardImageUrls(cardElement).primaryImage;

  const customProperties = {
    market_id: cardDataset.marketId,
    market_name: `${cardDataset.marketName}, ${cardDataset.stateAbbreviation}`,
    city: cardDataset.city,
    state: cardDataset.stateAbbreviation,
    zip: cardDataset.zip,
    builder_id: cardDataset.builderId,
    builder_name: cardDataset.brandName,
    community_id: cardDataset.communityId,
    community_name: cardDataset.communityName,
    primary_image_url: primaryImageUrl,
    spec_id: cardDataset.specId,
    is_paid: cardDataset.isBasic !== 'true',
    home_type: cardDataset.homeStatus,
    home_price: cardDataset.price,
    is_spotlight: !!cardDataset.wasMatchedAsSpotlightResult,
    community_type: cardDataset.communityType,
    is_neighborhood: isNeighborhoodPage(),
    is_schooldistrict: isSchoolDistrictPage(),
  };

  store.dispatch(segmentTrack({ eventName: 'Home Favorited', customProperties }));
};

manufacturedHomeSpecPendingFavoritesChanged$.subscribe(triggerManufacturedHomeSpecFavoritedEvent);
manufacturedHomePlanPendingFavoritesChanged$.subscribe(triggerManufacturedHomePlanFavoritedEvent);

// ! Community Favorited Event.

// observable created when there is an add to the redux store object "pendingSaves".
// this object contains a list of items that the user clicked but are not already saved.
// It will filter the actions to now when it changes.
const onPaidCustomLocationPendingFavoritesChanged$ = pendingListingSavesFavorites$.pipe(
  map((payload) => filterFavoritedByType(payload, FavoriteType.Community.toString())),
  filter(Boolean)
);

/**
 * The triggerPaidCustomLocationFavoritedEvent function is used to track the favorited event.
 * which happens when the user clicks in the fav button to save it.
 * This will gather the custom properties and dispatch the segment event
 * @param action Get the target element of the event
 *
 */
const triggerPaidCustomLocationFavoritedEvent = (payload) => {
  const itemId = payload.listingInformation.id;
  const cardElement = document.querySelector(`[data-paid-custom-location][data-id="${itemId}"]`);

  if (!cardElement) {
    return;
  }
  const cardDataset = cardElement.dataset;
  const primaryImageUrl =
    cardDataset.imageUrl !== '' ? cardDataset.imageUrl : getCardImageUrls(cardElement).primaryImage;

  const customProperties = {
    market_id: cardDataset.marketId,
    market_name: `${cardDataset.marketName}, ${cardDataset.stateAbbreviation}`,
    city: cardDataset.city,
    state: cardDataset.stateAbbreviation,
    zip: cardDataset.zip,
    builder_id: cardDataset.builderId,
    builder_name: cardDataset.brandName,
    community_id: cardDataset.communityId,
    community_name: cardDataset.communityName,
    primary_image_url: primaryImageUrl,
    is_paid: true,
    plan_id: cardDataset.planId,
    home_type: cardDataset.homeStatus,
    home_price: cardDataset.price,
    is_spotlight: !!cardDataset.wasMatchedAsSpotlightResult,
    community_type: cardDataset.communityType,
    is_neighborhood: isNeighborhoodPage(),
    is_schooldistrict: isSchoolDistrictPage(),
  };

  store.dispatch(segmentTrack({ eventName: 'Community Favorited', customProperties }));
};

onPaidCustomLocationPendingFavoritesChanged$.subscribe(triggerPaidCustomLocationFavoritedEvent);

// observable created when there is an add to the redux store object "pendingSaves".
// this object contains a list of items that the user clicked but are not already saved.
// It will filter the actions to now when it changes.
const onPaidBuildOnYourLotLocationPendingFavoritesChanged$ = pendingListingSavesFavorites$.pipe(
  map((payload) => filterFavoritedByType(payload, FavoriteType.Community.toString())),
  filter(Boolean)
);

/**
 * The triggerPaidBOYLLocationFavoritedEvent function is used to track the favorited event.
 * which happens when the user clicks in the fav button to save it.
 * This will gather the custom properties and dispatch the segment event
 * @param action Get the target element of the event
 *
 */
const triggerPaidBuildOnYourLotLocationFavoritedEvent = (payload) => {
  const itemId = payload.listingInformation.id;
  const cardElement = document.querySelector(`[data-paid-boyl-location][data-id="${itemId}"]`);

  if (!cardElement) {
    return;
  }
  const cardDataset = cardElement.dataset;
  const primaryImageUrl =
    cardDataset.imageUrl !== '' ? cardDataset.imageUrl : getCardImageUrls(cardElement).primaryImage;

  const customProperties = {
    market_id: cardDataset.marketId,
    market_name: `${cardDataset.marketName}, ${cardDataset.stateAbbreviation}`,
    city: cardDataset.city,
    state: cardDataset.stateAbbreviation,
    zip: cardDataset.zip,
    builder_id: cardDataset.builderId,
    builder_name: cardDataset.brandName,
    community_id: cardDataset.communityId,
    community_name: cardDataset.communityName,
    primary_image_url: primaryImageUrl,
    is_paid: true,
    plan_id: cardDataset.planId,
    home_type: cardDataset.homeStatus,
    home_price: cardDataset.price,
    is_spotlight: !!cardDataset.wasMatchedAsSpotlightResult,
    community_type: cardDataset.communityType,
    is_neighborhood: isNeighborhoodPage(),
    is_schooldistrict: isSchoolDistrictPage(),
  };

  store.dispatch(segmentTrack({ eventName: 'Community Favorited', customProperties }));
};

onPaidBuildOnYourLotLocationPendingFavoritesChanged$.subscribe(triggerPaidBuildOnYourLotLocationFavoritedEvent);

// observable created when there is an add to the redux store object "pendingSaves".
// this object contains a list of items that the user clicked but are not already saved.
// It will filter the actions to now when it changes.
const onBasicCustomLocationPendingFavoritesChanged$ = store$.pipe(
  map(selectPendingSaves),
  filter(Boolean),
  distinctUntilChanged(),
  pairwise(),
  map((payload) => filterFavoritedByType(payload, FavoriteType.Community.toString())),
  filter(Boolean)
);

/**
 * The triggerBasicCustomLocationFavoritedEvent function is used to track the favorited event.
 * which happens when the user clicks in the fav button to save it.
 * This will gather the custom properties and dispatch the segment event
 * @param action Get the target element of the event
 *
 */
const triggerBasicCustomLocationFavoritedEvent = (payload) => {
  const itemId = payload.listingInformation.id;
  const cardElement = document.querySelector(`[data-basic-custom-location][data-id="${itemId}"]`);

  if (!cardElement) {
    return;
  }
  const cardDataset = cardElement.dataset;
  const primaryImageUrl =
    cardDataset.imageUrl !== '' ? cardDataset.imageUrl : getCardImageUrls(cardElement).primaryImage;

  const customProperties = {
    market_id: cardDataset.marketId,
    market_name: `${cardDataset.marketName}, ${cardDataset.stateAbbreviation}`,
    city: cardDataset.city,
    state: cardDataset.stateAbbreviation,
    zip: cardDataset.zip,
    builder_id: cardDataset.builderId,
    builder_name: cardDataset.brandName,
    community_id: cardDataset.communityId,
    community_name: cardDataset.communityName,
    primary_image_url: primaryImageUrl,
    is_paid: false,
    plan_id: cardDataset.planId,
    home_type: cardDataset.homeStatus,
    home_price: cardDataset.price,
    is_spotlight: !!cardDataset.wasMatchedAsSpotlightResult,
    community_type: cardDataset.communityType,
    is_neighborhood: isNeighborhoodPage(),
    is_schooldistrict: isSchoolDistrictPage(),
  };

  store.dispatch(segmentTrack({ eventName: 'Community Favorited', customProperties }));
};

onBasicCustomLocationPendingFavoritesChanged$.subscribe(triggerBasicCustomLocationFavoritedEvent);

// observable created when there is an add to the redux store object "pendingSaves".
// this object contains a list of items that the user clicked but are not already saved.
// It will filter the actions to now when it changes.
const onBasicBuildOnYourLotLocationPendingFavoritesChanged$ = pendingListingSavesFavorites$.pipe(
  map((payload) => filterFavoritedByType(payload, FavoriteType.Community.toString())),
  filter(Boolean)
);

/**
 * The triggerBasicBOYLLocationFavoritedEvent function is used to track the favorited event.
 * which happens when the user clicks in the fav button to save it.
 * This will gather the custom properties and dispatch the segment event
 * @param action Get the target element of the event
 *
 */
const triggerBasicBuildOnYourLotLocationFavoritedEvent = (payload) => {
  const itemId = payload.listingInformation.id;
  const cardElement = document.querySelector(`[data-basic-boyl-location][data-id="${itemId}"]`);

  if (!cardElement) {
    return;
  }
  const cardDataset = cardElement.dataset;
  const primaryImageUrl =
    cardDataset.imageUrl !== '' ? cardDataset.imageUrl : getCardImageUrls(cardElement).primaryImage;

  const customProperties = {
    market_id: cardDataset.marketId,
    market_name: `${cardDataset.marketName}, ${cardDataset.stateAbbreviation}`,
    city: cardDataset.city,
    state: cardDataset.stateAbbreviation,
    zip: cardDataset.zip,
    builder_id: cardDataset.builderId,
    builder_name: cardDataset.brandName,
    community_id: cardDataset.communityId,
    community_name: cardDataset.communityName,
    primary_image_url: primaryImageUrl,
    is_paid: false,
    plan_id: cardDataset.planId,
    home_type: cardDataset.homeStatus,
    home_price: cardDataset.price,
    is_spotlight: !!cardDataset.wasMatchedAsSpotlightResult,
    community_type: cardDataset.communityType,
    is_neighborhood: isNeighborhoodPage(),
    is_schooldistrict: isSchoolDistrictPage(),
  };

  store.dispatch(segmentTrack({ eventName: 'Community Favorited', customProperties }));
};

onBasicBuildOnYourLotLocationPendingFavoritesChanged$.subscribe(triggerBasicBuildOnYourLotLocationFavoritedEvent);

// observable created when there is an add to the redux store object "pendingSaves".
// this object contains a list of items that the user clicked but are not already saved.
// It will filter the actions to now when it changes.
const manufacturedCommunityPendingFavoritesChanged$ = pendingListingSavesFavorites$.pipe(
  map((payload) => filterFavoritedByType(payload, FavoriteType.Community.toString())),
  filter(Boolean)
);

/**
 * The triggerBasicBOYLLocationFavoritedEvent function is used to track the favorited event.
 * which happens when the user clicks in the fav button to save it.
 * This will gather the custom properties and dispatch the segment event
 * @param action Get the target element of the event
 *
 */
const triggerManufacturedCommunityFavoritedEvent = (payload) => {
  const itemId = payload.listingInformation.id;
  const itemType = payload.listingInformation.isBasic !== 'true'
    ? 'paid' : 'basic';
  const cardElement = document.querySelector(`[data-${itemType}-manufactured-location][data-id="${itemId}"]`);

  if (!cardElement) {
    return;
  }
  const cardDataset = cardElement.dataset;
  const primaryImageUrl =
    cardDataset.imageUrl !== '' ? cardDataset.imageUrl : getCardImageUrls(cardElement).primaryImage;

  const customProperties = {
    market_id: cardDataset.marketId,
    market_name: `${cardDataset.marketName}, ${cardDataset.stateAbbreviation}`,
    city: cardDataset.city,
    state: cardDataset.stateAbbreviation,
    zip: cardDataset.zip,
    builder_id: cardDataset.builderId,
    builder_name: cardDataset.brandName,
    community_id: cardDataset.communityId,
    community_name: cardDataset.communityName,
    primary_image_url: primaryImageUrl,
    is_paid: cardDataset.isBasic !== 'true',
    plan_id: cardDataset.planId,
    home_type: cardDataset.homeStatus,
    home_price: cardDataset.price,
    is_spotlight: !!cardDataset.wasMatchedAsSpotlightResult,
    community_type: cardDataset.communityType,
    is_neighborhood: isNeighborhoodPage(),
    is_schooldistrict: isSchoolDistrictPage(),
  };

  store.dispatch(segmentTrack({ eventName: 'Community Favorited', customProperties }));
};

manufacturedCommunityPendingFavoritesChanged$.subscribe(triggerManufacturedCommunityFavoritedEvent);

/**
 * triggerCommunitySavedEvent: Triggers the event related when the favorite listing was already saved.
 * IMPORTANT: This event is not when the user clicks on the Favorite button, but when the listing was succesfully saved
 * on the server.
 * @param {Object} favoriteInformation Information related to the favorite.
 */
function triggerPaidCommunitySavedEvent(favoriteInformation) {
  const { listingInformation } = favoriteInformation;
  const isPaid = listingInformation.isBasic !== 'true';
  const customProperties = {
    market_id: listingInformation.marketId,
    market_name: `${listingInformation.marketName}, ${listingInformation.stateAbbreviation}`,
    builder_id: listingInformation.builderId,
    builder_name: listingInformation.brandName,
    community_id: listingInformation.id,
    community_name: listingInformation.name,
    community_status: listingInformation.marketingStatusLabel,
    primary_image_url: listingInformation.imageUrl,
    community_low_price: listingInformation.priceLow,
    community_high_price: listingInformation.priceHigh,
    city: listingInformation.city,
    state: listingInformation.stateAbbreviation,
    zip: listingInformation.zip,
    is_paid: isPaid,
    is_neighborhood: isNeighborhoodPage(),
    is_schooldistrict: isSchoolDistrictPage(),
  };

  store.dispatch(segmentTrack({ eventName: 'Community Saved', customProperties }));
}

const newCommunityListingSaved$ = newListingSaved$.pipe(
  filter((favoritedInformation) => favoritedInformation.type === FavoriteType.Community.toString())
);

newCommunityListingSaved$.subscribe(triggerPaidCommunitySavedEvent);

/**
 * triggerPaidSpecSavedEvent: Triggers the event related when the favorite paid spec listing was already saved.
 * IMPORTANT: This event is not when the user clicks on the Favorite button, but when the listing was succesfully saved
 * on the server.
 * @param {Object} favoriteInformation Information related to the favorite.
 */
function triggerPaidSpecSavedEvent(favoriteInformation) {
  const { listingInformation } = favoriteInformation;
  const isPaid = listingInformation.isBasic !== 'true';
  const customProperties = {
    market_id: listingInformation.marketId,
    market_name: `${listingInformation.marketName}, ${listingInformation.stateAbbreviation}`,
    builder_id: listingInformation.builderId,
    builder_name: listingInformation.brandName,
    community_id: listingInformation.communityId,
    community_name: listingInformation.communityName,
    primary_image_url: listingInformation.imageUrl,
    home_type: listingInformation.homeStatus,
    home_price: listingInformation.price,
    spec_id: listingInformation.id,
    city: listingInformation.city,
    state: listingInformation.stateAbbreviation,
    zip: listingInformation.zip,
    is_paid: isPaid,
    is_neighborhood: isNeighborhoodPage(),
    is_schooldistrict: isSchoolDistrictPage(),
  };

  store.dispatch(segmentTrack({ eventName: 'Home Saved', customProperties }));
}

const newPaidSpecListingSaved$ = newListingSaved$.pipe(
  filter((favoritedInformation) => favoritedInformation.type === FavoriteType.Spec.toString())
);

newPaidSpecListingSaved$.subscribe(triggerPaidSpecSavedEvent);

/**
 * triggerPaidPlanSavedEvent: Triggers the event related when the favorite paid plan listing was already saved.
 * IMPORTANT: This event is not when the user clicks on the Favorite button, but when the listing was succesfully saved
 * on the server.
 * @param {Object} favoriteInformation Information related to the favorite.
 */
function triggerPaidPlanSavedEvent(favoriteInformation) {
  const { listingInformation } = favoriteInformation;
  const isPaid = listingInformation.isBasic !== 'true';
  const customProperties = {
    market_id: listingInformation.marketId,
    market_name: `${listingInformation.marketName}, ${listingInformation.stateAbbreviation}`,
    builder_id: listingInformation.builderId,
    builder_name: listingInformation.brandName,
    community_id: listingInformation.communityId,
    community_name: listingInformation.communityName,
    primary_image_url: listingInformation.imageUrl,
    home_type: listingInformation.homeStatus,
    home_price: listingInformation.price,
    plan_id: listingInformation.id,
    city: listingInformation.city,
    state: listingInformation.stateAbbreviation,
    zip: listingInformation.zip,
    is_paid: isPaid,
    is_neighborhood: isNeighborhoodPage(),
    is_schooldistrict: isSchoolDistrictPage(),
  };

  store.dispatch(segmentTrack({ eventName: 'Home Saved', customProperties }));
}

/**
 * Triggers the related event when a community has already been removed from the list of favorite communities.
 * @param {any} favoriteInformation Information about the removed card.
 */
function triggerCommunityRemovedEvent(favoriteInformation) {
  const itemId = favoriteInformation.listingId;
  const cardElement = document.querySelector(`[data-id="${itemId}"]`);

  if (!cardElement) {
    return;
  }
  const cardDataset = cardElement.dataset;
  const primaryImageUrl =
    cardDataset.imageUrl !== '' ? cardDataset.imageUrl : getCardImageUrls(cardElement).primaryImage;
  const isPaid = cardDataset.isBasic !== 'true';
  const customProperties = {
    market_id: cardDataset.marketId,
    market_name: `${cardDataset.marketName}, ${cardDataset.stateAbbreviation}`,
    builder_id: cardDataset.builderId,
    builder_name: cardDataset.brandName,
    community_id: cardDataset.id,
    community_name: cardDataset.name,
    community_status: cardDataset.marketingStatusLabel,
    primary_image_url: primaryImageUrl,
    community_low_price: cardDataset.priceLow,
    community_high_price: cardDataset.priceHigh,
    city: cardDataset.city,
    state: cardDataset.stateAbbreviation,
    zip: cardDataset.zip,
    is_paid: isPaid,
  };

  store.dispatch(segmentTrack({ eventName: 'Community Removed', customProperties }));
}

const removedCommunity$ = removedFavorites$.pipe(
  filter((favoritedInformation) => favoritedInformation.type === FavoriteType.Community.toString())
);

removedCommunity$.subscribe(triggerCommunityRemovedEvent);

/**
 * Triggers the related event when a home has already been removed from the list of favorite communities.
 * @param {any} favoriteInformation Information about the removed card.
 */
function triggerHomeRemovedEvent(favoriteInformation) {
  const itemId = favoriteInformation.listingId;
  const cardElement = document.querySelector(`[data-id="${itemId}"]`);

  if (!cardElement) {
    return;
  }
  const cardDataset = cardElement.dataset;
  const primaryImageUrl =
    cardDataset.imageUrl !== '' ? cardDataset.imageUrl : getCardImageUrls(cardElement).primaryImage;
  const isPaid = cardDataset.isBasic !== 'true';
  const customProperties = {
    market_id: cardDataset.marketId,
    market_name: `${cardDataset.marketName}, ${cardDataset.stateAbbreviation}`,
    builder_id: cardDataset.builderId,
    builder_name: cardDataset.brandName,
    community_id: cardDataset.communityId,
    community_name: cardDataset.communityName,
    primary_image_url: primaryImageUrl,
    home_type: cardDataset.homeStatus,
    home_price: cardDataset.price,
    plan_id: cardDataset.planId,
    spec_id: cardDataset.specId,
    city: cardDataset.city,
    state: cardDataset.stateAbbreviation,
    zip: cardDataset.zip,
    is_paid: isPaid,
  };

  store.dispatch(segmentTrack({ eventName: 'Home Removed', customProperties }));
}

const removedSpecHome$ = removedFavorites$.pipe(
  filter((favoritedInformation) => favoritedInformation.type === 'Spec')
);
const removedPlanHome$ = removedFavorites$.pipe(
  filter((favoritedInformation) => favoritedInformation.type === 'Plan')
);

removedSpecHome$.subscribe(triggerHomeRemovedEvent);
removedPlanHome$.subscribe(triggerHomeRemovedEvent);

/**
 * Triggers the map marker clicked event for community markers.
 * 
 * @param {Object} marker - The marker object.
 */
function triggerCommunityMapMarkerClickedEvent(marker) {
  const customProperties = {
    market_id: marker.MarketId,
    builder_id: marker.BuilderId,
    builder_name: marker.BrandName,
    community_high_price: marker.PriceHigh,
    community_low_price: marker.PriceLow,
    community_id: marker.Id,
    community_name: marker.Name,
    community_status: marker.MarketingStatus.Name,
    image_1_url: marker.Thumbnail,
    is_zero_results: isNotResultPage(),
    page_type: 'Community_Results',
    primary_image_url: marker.Thumbnail,
    city: marker.City,
    state: marker.StateAbbreviation,
    zip: marker.Zip,
    is_paid: !marker.IsBasic,
    is_spotlight: !!marker.WasMatchedAsSpotlightResult,
    community_type: marker.CommunityProjectType.Key,
  };

  store.dispatch(segmentTrack({ eventName: 'Community Result Map Displayed', customProperties }));
}

/**
 * Triggers the map marker clicked event for community markers.
 *
 * @param {Object} marker - The marker object.
 */
markerSummaryCardClicked$
  .pipe(
    filter((listings) => Boolean(listings?.length)),
    map(listings => listings[0]),
    filter(
      (marker) => marker.Type === MarkerType.HousingDevelopment.toString()
    ),
    distinctUntilChanged()
  )
  .subscribe(triggerCommunityMapMarkerClickedEvent);

/**
 * Triggers the map marker clicked event for home markers.
 * 
 * @param {Object} marker - The marker object.
 */
function triggerPlanMapMarkerClickedEvent(marker) {
  const customProperties = {
    market_id: marker.MarketId,
    builder_id: marker.BuilderId,
    builder_name: marker.BrandName,
    community_id: marker.CommunityId,
    community_name: marker.CommunityName,
    image_1_url: marker.Thumbnail,
    is_zero_results: isNotResultPage(),
    market_name: `${marker.MarketName},${marker.StateAbbreviation}`,
    page_type: 'Homes_Results',
    primary_image_url: marker.Thumbnail,
    plan_id: marker.Id,
    home_price: marker.Price,
    city: marker.City,
    state: marker.StateAbbreviation,
    zip: marker.Zip,
    is_paid: !marker.IsBasic,
    is_spotlight: !!marker.WasMatchedAsSpotlightResult,
    community_type: marker.CommunityProjectType.Key,
  };

  store.dispatch(segmentTrack({ eventName: 'Home Result Map Displayed', customProperties }));
}

/**
 * Triggers the map marker clicked event for plan unit markers.
 *
 * @param {Object} marker - The marker object.
 */
markerSummaryCardClicked$
  .pipe(
    filter((listings) => Boolean(listings?.length)),
    map(listings => listings[0]),
    filter((marker) => marker.Type === MarkerType.PlanUnit.toString()),
    distinctUntilChanged()
  )
  .subscribe(triggerPlanMapMarkerClickedEvent);

/**
 * Triggers the map marker clicked event for home markers.
 * 
 * @param {Object} marker - The marker object.
 */
function triggerSpecMapMarkerClickedEvent(marker) {
  const customProperties = {
    market_id: marker.MarketId,
    builder_id: marker.BuilderId,
    builder_name: marker.BrandName,
    community_id: marker.CommunityId,
    community_name: marker.CommunityName,
    image_1_url: marker.Thumbnail,
    is_zero_results: isNotResultPage(),
    market_name: `${marker.MarketName},${marker.StateAbbreviation}`,
    page_type: 'Homes_Results',
    primary_image_url: marker.Thumbnail,
    spec_id: marker.Id,
    home_price: marker.Price,
    city: marker.City,
    state: marker.StateAbbreviation,
    zip: marker.Zip,
    is_paid: !marker.IsBasic,
    is_spotlight: !!marker.WasMatchedAsSpotlightResult,
    community_type: marker.CommunityProjectType.Key,
  };

  store.dispatch(segmentTrack({ eventName: 'Home Result Map Displayed', customProperties }));
}

/**
 * Triggers the map marker clicked event for spec unit markers.
 *
 * @param {Object} marker - The marker object.
 */
markerSummaryCardClicked$
  .pipe(
    filter((listings) => Boolean(listings?.length)),
    map(listings => listings[0]),
    filter((marker) => marker.Type === MarkerType.SpecUnit.toString()),
    distinctUntilChanged()
  )
  .subscribe(triggerSpecMapMarkerClickedEvent);

/**
 * Triggers the map marker clicked event for community markers.
 * 
 * @param {Object} marker - The marker object.
 */
function triggerCommunitySummaryCardLinkClickedEvent(marker) {
  const customProperties = {
    market_id: marker.MarketId,
    builder_id: marker.BuilderId,
    builder_name: marker.BrandName,
    community_high_price: marker.PriceHigh,
    community_low_price: marker.PriceLow,
    community_id: marker.Id,
    community_name: marker.Name,
    community_status: marker.MarketingStatus.Name,
    image_1_url: marker.Thumbnail,
    is_zero_results: isNotResultPage(),
    page_type: 'Community_Results',
    primary_image_url: marker.Thumbnail,
    city: marker.City,
    state: marker.StateAbbreviation,
    zip: marker.Zip,
    is_paid: !marker.IsBasic,
    is_spotlight: !!marker.WasMatchedAsSpotlightResult,
    community_type: marker.CommunityProjectType.Key,
  };

  store.dispatch(segmentTrack({ eventName: 'Community Result Map Selected', customProperties }));
}

/**
 * Triggers the map marker clicked event for community summary cards.
 */
summaryCardLinkClicked$.pipe(
  filter(marker => Boolean(marker)),
  filter(marker => marker.Type === MarkerType.HousingDevelopment.toString())
).subscribe(triggerCommunitySummaryCardLinkClickedEvent);

/**
 * Triggers the map marker clicked event for home markers.
 * 
 * @param {Object} marker - The marker object.
 */
function triggerPlanSummaryCardLinkClickedEvent(marker) {
  const customProperties = {
    market_id: marker.MarketId,
    builder_id: marker.BuilderId,
    builder_name: marker.BrandName,
    community_id: marker.CommunityId,
    community_name: marker.CommunityName,
    image_1_url: marker.Thumbnail,
    is_zero_results: isNotResultPage(),
    market_name: `${marker.MarketName},${marker.StateAbbreviation}`,
    page_type: 'Homes_Results',
    primary_image_url: marker.Thumbnail,
    plan_id: marker.Id,
    home_price: marker.Price,
    city: marker.City,
    state: marker.StateAbbreviation,
    zip: marker.Zip,
    is_paid: !marker.IsBasic,
    is_spotlight: !!marker.WasMatchedAsSpotlightResult,
    community_type: marker.CommunityProjectType.Key,
  };

  store.dispatch(segmentTrack({ eventName: 'Home Result Map Selected', customProperties }));
}

/**
 * Triggers the map card link event for plan summary cards.
 */
summaryCardLinkClicked$.pipe(
  filter(marker => Boolean(marker)),
  filter(marker => marker.Type === MarkerType.PlanUnit.toString())
).subscribe(triggerPlanSummaryCardLinkClickedEvent);

/**
 * Triggers the map card link clicked event for spec markers.
 * 
 * @param {Object} marker - The marker object.
 */
function triggerSpecSummaryCardLinkClickedEvent(marker) {
  const customProperties = {
    market_id: marker.MarketId,
    builder_id: marker.BuilderId,
    builder_name: marker.BrandName,
    community_id: marker.CommunityId,
    community_name: marker.CommunityName,
    image_1_url: marker.Thumbnail,
    is_zero_results: isNotResultPage(),
    market_name: `${marker.MarketName},${marker.StateAbbreviation}`,
    page_type: 'Homes_Results',
    primary_image_url: marker.Thumbnail,
    spec_id: marker.Id,
    home_price: marker.Price,
    city: marker.City,
    state: marker.StateAbbreviation,
    zip: marker.Zip,
    is_paid: !marker.IsBasic,
    is_spotlight: !!marker.WasMatchedAsSpotlightResult,
    community_type: marker.CommunityProjectType.Key,
  };

  store.dispatch(segmentTrack({ eventName: 'Home Result Map Selected', customProperties }));
}

/**
 * Triggers the map card link clicked event for spec summary cards.
 */
summaryCardLinkClicked$.pipe(
  filter(marker => Boolean(marker)),
  filter(marker => marker.Type === MarkerType.SpecUnit.toString())
).subscribe(triggerSpecSummaryCardLinkClickedEvent);

/**
 * Triggers the community map card CTA clicked event.
 * 
 * @param {Object} marker - The marker object.
 */
function triggerCommunitySummaryCardCallToActionClickEvent(detail) {
  const { marker } = detail;
  
  const customProperties = {
    market_id: marker.MarketId,
    brand_name: marker.brandName,
    id: marker.Id,
    name: marker.Name,
    type: 'community',
    price: marker.Price,
    city: marker.City,
    state: marker.StateAbbreviation,
    zip: marker.Zip,
    is_paid: !marker.IsBasic,
    is_spotlight: !!marker.WasMatchedAsSpotlightResult,
    community_type: marker.CommunityProjectType.Key,
    is_neighborhood: isNeighborhoodPage(),
    is_schooldistrict: isSchoolDistrictPage(),
  };

  setTimeout(()=> store.dispatch(segmentTrack({ eventName: 'Lead Button Clicked', customProperties })), 0);
}

/**
 * Triggers the map card CTA clicked event for community summary cards.
 */
summaryCardCtaClicked$.pipe(
  filter((detail) => Boolean(detail.marker)),
  filter((detail) => detail.marker.Type === MarkerType.HousingDevelopment.toString()),
  distinctUntilChanged(),
).subscribe(triggerCommunitySummaryCardCallToActionClickEvent);

/**
 * Triggers the community map card CTA clicked event.
 * 
 * @param {Object} marker - The marker object.
 */
function triggerPlanSummaryCardCallToActionClickEvent(detail) {
  const { marker } = detail;
  
  const customProperties = {
    market_id: marker.MarketId,
    brand_name: marker.brandName,
    id: marker.Id,
    name: marker.Name,
    type: 'plan',
    price: marker.Price,
    city: marker.City,
    state: marker.StateAbbreviation,
    zip: marker.Zip,
    is_paid: !marker.IsBasic,
    is_spotlight: !!marker.WasMatchedAsSpotlightResult,
    community_type: marker.CommunityProjectType.Key,
    is_neighborhood: isNeighborhoodPage(),
    is_schooldistrict: isSchoolDistrictPage(),
  };

  setTimeout(()=> store.dispatch(segmentTrack({ eventName: 'Lead Button Clicked', customProperties })), 0);
}
/**
 * Triggers the plan map card CTA clicked event.
 */
summaryCardCtaClicked$.pipe(
  filter((detail) => Boolean(detail.marker)),
  filter((detail) => detail.marker.Type === MarkerType.PlanUnit.toString()),
  distinctUntilChanged(),
).subscribe(triggerPlanSummaryCardCallToActionClickEvent);

/**
 * Triggers the community map card CTA clicked event.
 * 
 * @param {Object} marker - The marker object.
 */
function triggerSpecSummaryCardCallToActionClickEvent(detail) {
  const { marker } = detail;
  
  const customProperties = {
    market_id: marker.MarketId,
    brand_name: marker.brandName,
    id: marker.Id,
    name: marker.Name,
    type: 'spec',
    price: marker.Price,
    city: marker.City,
    state: marker.StateAbbreviation,
    zip: marker.Zip,
    is_paid: !marker.IsBasic,
    is_spotlight: !!marker.WasMatchedAsSpotlightResult,
    community_type: marker.CommunityProjectType.Key,
    is_neighborhood: isNeighborhoodPage(),
    is_schooldistrict: isSchoolDistrictPage(),
  };

  setTimeout(()=> store.dispatch(segmentTrack({ eventName: 'Lead Button Clicked', customProperties })), 0);
}

/**
 * Triggers the spec map card CTA clicked event.
 */
summaryCardCtaClicked$.pipe(
  filter((detail) => Boolean(detail.marker)),
  filter((detail) => detail.marker.Type === MarkerType.SpecUnit.toString()),
  distinctUntilChanged(),
).subscribe(triggerSpecSummaryCardCallToActionClickEvent);

const newPaidPlanListingSaved$ = newListingSaved$.pipe(
  filter((favoritedInformation) => favoritedInformation.type === FavoriteType.Plan.toString())
);

newPaidPlanListingSaved$.subscribe(triggerPaidPlanSavedEvent);

const sortChanged$ = store$.pipe(map(selectSorting), distinctUntilChanged(), skip(1));

const sortSelectElement = document.querySelector('[data-sort-select]');

/**
 * getSortingLabel: Gets the label to be used on the sort value on Segment events.
 * @param {object} sortingInformation Information related to sorting.
 * @returns The label to be used related to the sort value.
 */
function getSortingLabel(sortingInformation) {
  const sortingValue = `${sortingInformation?.sortBy},${sortingInformation?.sortOrder}`;
  const sortingOption = sortSelectElement.querySelector(`[value="${sortingValue}"]`);
  return sortingOption?.textContent;
}

/**
 * trackResultsSortedEvent: Tracks the sorted event for the searches.
 * @param {object} sorting Information related to the sort parameters.
 */
function trackResultsSortedEvent(sorting) {
  const customProperties = {
    sort_value: getSortingLabel(sorting),
  };

  store.dispatch(segmentTrack({ eventName: 'Results Sorted', customProperties }));
}

sortChanged$.subscribe(trackResultsSortedEvent);

// #endregion List View Section

// #region TabSelect
const homesSelector = '[data-product-type="homes"]';
const communitiesSelector = '[data-product-type="communities"]';

const tabsContainer = document.querySelector('[data-result-type-bar]');
const tabsContainerClicks$ = fromEvent(tabsContainer, 'click');

const homesButtonClicks$ = tabsContainerClicks$.pipe(
  filter((event) => event.target.closest(homesSelector)),
  map((event) => event.target)
);

const communitiesButtonClicks$ = tabsContainerClicks$.pipe(
  filter((event) => event.target.closest(communitiesSelector)),
  map((event) => event.target)
);

const triggerHomesTabClickEvent = () => {
  const customProperties = {
    page_type: PageType.Homes.toString(),
};
  store.dispatch(segmentTrack({ eventName: 'View Homes Clicked', customProperties }));
};

homesButtonClicks$.subscribe(triggerHomesTabClickEvent);

const triggerCommunitiesTabClickEvent = () => {
  const customProperties = {
    page_type: PageType.Communities.toString(),
};
  store.dispatch(segmentTrack({ eventName: 'View Communities Clicked', customProperties }));
};

communitiesButtonClicks$.subscribe(triggerCommunitiesTabClickEvent);

// #end region TabSelect

// #region Pageview
/**
 *  ! When URL change, dispatch a pageview
 */
const triggerPageViewEvent = () => store.dispatch(segmentPageView({}));
const onUrlChange$ = store$.pipe(map(selectCurrentUrl), filter(Boolean), distinctUntilChanged());

/**
 *  Observable checking the changes in the Hounsing content.
 */
const onHousingProductLocationFetchStatus$ = store$.pipe(
  map(selectHousingProductLocationFetchStatus),
  distinctUntilChanged());

/**
 *  Observable checking the changes in the listing counts selector, 
 * checks if the ajax request is succes and the gets the counts value.
 */
const countsChange$ = store$.pipe(
  map(selectFetchCountsStatusAndsCounts),
  distinctUntilKeyChanged('resultCountsStatus'));

/**
 *  Observable checking the URL changes, the Housing product content change and the counts change
 *  in order to trigger the segment page view is the counts are greater than 0.
 */
combineLatest([onUrlChange$, onHousingProductLocationFetchStatus$, countsChange$])
  .pipe(
   map(([currentUrl, housingProductFetchStatus, counts]) => ({currentUrl, housingProductFetchStatus, counts})),
   filter(({counts}) => counts.resultCountsStatus !== LoadingStatus.Loading),
   filter(({housingProductFetchStatus}) => housingProductFetchStatus !== LoadingStatus.Loading),
   filter(({counts}) => counts.listingCounts > 0))
  .subscribe(triggerPageViewEvent);

const triggerPageViewNoResultEvent = () => store.dispatch(segmentPageView(
  { customProperties: { is_zero_results: true } }));

/**
* Observable checking the counts and the Housing product content change
* in order to trigger the No results page view.
*/
combineLatest([countsChange$, onHousingProductLocationFetchStatus$])
  .pipe(filter(([,  housingProductStatus ]) => housingProductStatus !== LoadingStatus.Loading),
  filter(([counts]) => counts.resultCountsStatus !== LoadingStatus.Loading),
  filter(([counts]) => counts.listingCounts === 0))
  .subscribe(triggerPageViewNoResultEvent);

/**
* trackRealtorQuestionAnswerEvent: Tracks the sorted event for the realtor dropdown.
*/
function getRealtorQuestionAnswerText() {
  const realtorSelect = document.querySelector('[name="realtorQuestion"]');
  let realtorQuestionAnswer = '';
  if (realtorSelect && realtorSelect.selectedIndex > 0) {
    realtorQuestionAnswer = realtorSelect.options[realtorSelect.selectedIndex].text;
  }
  return realtorQuestionAnswer;
}

// #region

// #region Brochure Modal
/**
 *  ! Brochure Modal Section
 *  All events related elements located in brochure modal should be placed in this region
 */

/**
 * triggerPaidCommunityModalSubmitted: Triggers the event when the modal form is submitted.
 * This event only applies for the following forms:
 * - Free Brochure
 * - Get More Information
 * @param {object} modalInformation The information related to the modal opened.
 */
function triggerPaidCommunityModalSubmitted(modalInformation) {
  const { listingInformation } = modalInformation;

  const customProperties = {
    market_id: listingInformation.marketId,
    market_name: !listingInformation.marketName || !listingInformation.stateAbbreviation
               ? undefined : `${listingInformation.marketName}, ${listingInformation.stateAbbreviation}`,
    builder_id: listingInformation.builderId,
    builder_name: listingInformation.brandName,
    community_id: listingInformation.id,
    community_name: listingInformation.communityName,
    community_status: listingInformation.marketingStatusLabel,
    primary_image_url: listingInformation.imageUrl,
    community_low_price: listingInformation.priceLow,
    community_high_price: listingInformation.priceHigh,
    city: listingInformation.city,
    state: listingInformation.stateAbbreviation,
    zip: listingInformation.zip,
    button_action: modalInformation.buttonAction,
    is_paid: true,
    is_spotlight: !!listingInformation.wasMatchedAsSpotlightResult,
    community_type: listingInformation.communityType,
    realtor_question: getRealtorQuestionAnswerText(),
    is_neighborhood: isNeighborhoodPage(),
    is_schooldistrict: isSchoolDistrictPage(),
    position: +listingInformation.index + 1,
  };

  store.dispatch(segmentTrack({ eventName: 'Lead Modal Form Submitted', customProperties }));
}

paidCommunityModalSubmission$.subscribe(triggerPaidCommunityModalSubmitted);
paidManufacturedLocationModalSubmission$.subscribe(triggerPaidCommunityModalSubmitted);
/**
 * triggerBasicCommunityModalSubmitted: Triggers the event when the modal form is submitted.
 * This event only applies for the following forms:
 * - Learn More
 * @param {object} modalInformation The information related to the modal opened.
 */
function triggerBasicCommunityModalSubmitted(modalInformation) {
  const { listingInformation } = modalInformation;
  const customProperties = {
    market_id: listingInformation.marketId,
    market_name: !listingInformation.marketName || !listingInformation.stateAbbreviation
               ? undefined : `${listingInformation.marketName}, ${listingInformation.stateAbbreviation}`,
    builder_id: listingInformation.builderId,
    builder_name: listingInformation.brandName,
    community_id: listingInformation.id,
    community_name: listingInformation.communityName,
    community_status: listingInformation.marketingStatusLabel,
    primary_image_url: listingInformation.imageUrl,
    community_low_price: listingInformation.priceLow,
    community_high_price: listingInformation.priceHigh,
    city: listingInformation.city,
    state: listingInformation.stateAbbreviation,
    zip: listingInformation.zip,
    button_action: modalInformation.buttonAction,
    is_paid: false,
    is_spotlight: !!listingInformation.wasMatchedAsSpotlightResult,
    community_type: listingInformation.communityType,
    is_neighborhood: isNeighborhoodPage(),
    is_schooldistrict: isSchoolDistrictPage(),
    position: +listingInformation.index + 1,
  };

  store.dispatch(segmentTrack({ eventName: 'Lead Modal Form Submitted', customProperties }));
}

basicCommunityModalSubmission$.subscribe(triggerBasicCommunityModalSubmitted);


/**
 * Triggers the events for the recommended communities that were selected.
 * @param {Object} modalInformation Information related to the modal.
 */
function triggerRecommendedCommunitySubmittedEvents(modalInformation) {
  const { selectedRecommendedCommunities, buttonAction, listingInformation } = modalInformation;
  selectedRecommendedCommunities.forEach((community) => {
    const customProperties = {
      market_id: community.MarketId,
      market_name: `${community.MarketName}, ${community.StateAbbreviation}`,
      builder_id: community.BuilderId,
      builder_name: community.BrandName,
      community_id: community.CommunityId,
      community_name: community.CommunityName,
      community_status: community.MarketingStatusLabel,
      primary_image_url: community.ImageUrl,
      community_low_price: community.PriceLow,
      community_high_price: community.PriceHigh,
      city: community.City,
      state: community.StateAbbreviation,
      zip: community.Zip,
      button_action: buttonAction,
      is_paid: !community.isBasic,
      // setting is_spotlight to false as spotlight is a concept that does not apply to recommended communities
      // clarification around this is needed, context in ticket: NHS20-6740
      is_spotlight: false,
      community_type: community.CommunityProjectType,
      is_neighborhood: isNeighborhoodPage(),
      is_schooldistrict: isSchoolDistrictPage(),
      position: +listingInformation.index + 1,
    };

    store.dispatch(segmentTrack({ eventName: 'Rec Community Lead Modal Submitted', customProperties }));
  });
}

recommendedCommunitiesSubmitted$.subscribe(triggerRecommendedCommunitySubmittedEvents);
recommendedCommunitiesSubmittedBasic$.subscribe(triggerRecommendedCommunitySubmittedEvents);

/**
 * triggerPaidHomeModalSubmitted: Triggers the event when the modal form is submitted.
 * This event only applies for the following forms:
 * - Free Brochure
 * - Get More Information
 * @param {object} modalInformation The information related to the modal opened.
 */
function triggerPaidHomeModalSubmitted(modalInformation) {
  const { listingInformation } = modalInformation;
  const customProperties = {
    builder_id: listingInformation.builderId,
    builder_name: listingInformation.brandName,
    button_action: modalInformation.buttonAction,
    city: listingInformation.city,
    community_id: listingInformation.communityId,
    community_name: listingInformation.communityName,
    home_price: listingInformation.price,
    home_type: listingInformation.homeStatus,
    is_paid: true,
    market_id: listingInformation.marketId,
    market_name: !listingInformation.marketName || !listingInformation.stateAbbreviation
               ? undefined : `${listingInformation.marketName}, ${listingInformation.stateAbbreviation}`,
    plan_id: listingInformation.planId,
    primary_image_url: listingInformation.imageUrl,
    state: listingInformation.stateAbbreviation,
    spec_id: listingInformation.specId,
    zip: listingInformation.zip,
    is_spotlight: !!listingInformation.wasMatchedAsSpotlightResult,
    community_type: listingInformation.communityType,
    realtor_question: getRealtorQuestionAnswerText(),
    is_neighborhood: isNeighborhoodPage(),
    is_schooldistrict: isSchoolDistrictPage(),
    position: +listingInformation.index + 1,
  };
  
  customProperties[
    listingInformation.type === "spec" ? "specName" : "planName"
  ] = listingInformation.name;

  store.dispatch(segmentTrack({ eventName: 'Lead Modal Form Submitted', customProperties }));
}

paidHomeModalSubmission$.subscribe(triggerPaidHomeModalSubmitted);
paidManufacturedPlanModalSubmission$.subscribe(triggerPaidHomeModalSubmitted);
paidManufacturedSpecModalSubmission$.subscribe(triggerPaidHomeModalSubmitted);

/**
 * triggerBasicHomeModalSubmitted: Triggers the event when the modal form is submitted.
 * This event only applies for the following forms:
 * - Learn More
 * @param {object} modalInformation The information related to the modal opened.
 */
function triggerBasicHomeModalSubmitted(modalInformation) {
  const { listingInformation } = modalInformation;
  const customProperties = {
    builder_id: listingInformation.builderId,
    builder_name: listingInformation.brandName,
    button_action: modalInformation.buttonAction,
    city: listingInformation.city,
    community_id: listingInformation.communityId,
    community_name: listingInformation.communityName,
    home_price: listingInformation.price,
    home_type: listingInformation.homeStatus,
    is_paid: false,
    listing_id: listingInformation.listingId,
    market_id: listingInformation.marketId,
    market_name: !listingInformation.marketName || !listingInformation.stateAbbreviation
               ? undefined : `${listingInformation.marketName}, ${listingInformation.stateAbbreviation}`,
    plan_id: listingInformation.planId,
    primary_image_url: listingInformation.imageUrl,
    state: listingInformation.stateAbbreviation,
    spec_id: listingInformation.specId,
    zip: listingInformation.zip,
    is_spotlight: !!listingInformation.wasMatchedAsSpotlightResult,
    community_type: listingInformation.communityType,
    is_neighborhood: isNeighborhoodPage(),
    is_schooldistrict: isSchoolDistrictPage(),
    position: +listingInformation.index + 1,
  };

  customProperties[
    listingInformation.type === "spec" ? "specName" : "planName"
  ] = listingInformation.name;

  store.dispatch(segmentTrack({ eventName: 'Lead Modal Form Submitted', customProperties }));
}

basicPlanModalSubmission$.subscribe(triggerBasicHomeModalSubmitted);
basicSpecModalSubmission$.subscribe(triggerBasicHomeModalSubmitted);

/**
 * triggerBasicListingModalSubmitted: Triggers the event when the modal form is submitted.
 * This event only applies for the following forms:
 * - Learn More
 * @param {object} modalInformation The information related to the modal opened.
 */
function triggerBasicListingModalSubmitted(modalInformation) {
  const { listingInformation } = modalInformation;
  const customProperties = {
    button_action: modalInformation.buttonAction,
    city: listingInformation.city,
    community_id: listingInformation.communityId,
    community_name: listingInformation.communityName,
    home_price: listingInformation.price,
    is_paid: false,
    listing_id: listingInformation.id,
    market_id: listingInformation.marketId,
    market_name: !listingInformation.marketName || !listingInformation.stateAbbreviation
               ? undefined : `${listingInformation.marketName}, ${listingInformation.stateAbbreviation}`,
    primary_image_url: listingInformation.imageUrl,
    state: listingInformation.stateAbbreviation,
    zip: listingInformation.zip,
    is_spotlight: !!listingInformation.wasMatchedAsSpotlightResult,
    community_type: listingInformation.communityType,
    is_neighborhood: isNeighborhoodPage(),
    is_schooldistrict: isSchoolDistrictPage(),
    position: +listingInformation.index + 1,
  };

  store.dispatch(segmentTrack({ eventName: 'Lead Modal Form Submitted', customProperties }));
}

basicListingModalSubmission$.subscribe(triggerBasicListingModalSubmitted);

// #endregion Brochure Modal

// #region

/**
 * ! IDENTIFY EVENTS
 */

const triggerIdentifySavedCommunity = () => {
  store.dispatch(segmentIdentify({ eventName: 'Community Saved' }));
};

newCommunityListingSaved$.subscribe(triggerIdentifySavedCommunity);

const triggerIdentifyCommunityModalSubmission = () => {
  store.dispatch(segmentIdentify({ eventName: 'Lead Modal Form Submitted' }));
};

paidCommunityModalSubmission$.subscribe(triggerIdentifyCommunityModalSubmission);
paidManufacturedLocationModalSubmission$.subscribe(triggerIdentifyCommunityModalSubmission);
basicCommunityModalSubmission$.subscribe(triggerIdentifyCommunityModalSubmission);

const triggerIdentifySavedSpec = () => {
  store.dispatch(segmentIdentify({ eventName: 'Home Saved' }));
};

newPaidSpecListingSaved$.subscribe(triggerIdentifySavedSpec);

const triggerIdentifySavedPlan = () => {
  store.dispatch(segmentIdentify({ eventName: 'Home Saved' }));
};

newPaidPlanListingSaved$.subscribe(triggerIdentifySavedPlan);

const triggerIdentifyHomeModalSubmission = () => {
  store.dispatch(segmentIdentify({ eventName: 'Lead Modal Form Submitted' }));
};

paidHomeModalSubmission$.subscribe(triggerIdentifyHomeModalSubmission);
paidManufacturedPlanModalSubmission$.subscribe(triggerIdentifyHomeModalSubmission);
paidManufacturedSpecModalSubmission$.subscribe(triggerIdentifyHomeModalSubmission);
basicPlanModalSubmission$.subscribe(triggerIdentifyHomeModalSubmission);
basicSpecModalSubmission$.subscribe(triggerIdentifyHomeModalSubmission);
basicListingModalSubmission$.subscribe(triggerIdentifyHomeModalSubmission);

const triggerIdentifyRemovedCommunityFavorite = () => {
  store.dispatch(segmentIdentify({ eventName: 'Community Removed' }));
};

removedCommunity$.subscribe(triggerIdentifyRemovedCommunityFavorite);

const triggerIdentifyRemovedHomeFavorite = () => {
  store.dispatch(segmentIdentify({ eventName: 'Home Removed' }));
};

removedSpecHome$.subscribe(triggerIdentifyRemovedHomeFavorite);
removedPlanHome$.subscribe(triggerIdentifyRemovedHomeFavorite);

/**
 * Returns if the listing is price reduction or not.
 * 
 * @param {string} priceReductionLabel - The price reduction label.
 */
const isPriceReduction = (priceReductionLabel) => priceReductionLabel === "Y";

/**
 * Triggers the map marker clicked event for community markers.
 * 
 * @param {Object} marker - The marker object.
 */
function triggerCommunityMapMarkerUrlOpenedEvent(data) {
  const { marker } = data;

  const customProperties = {
    id: marker.id,
    name: marker.metaData.communityName,
    type: 'community',
    price: marker.metaData.priceLow,
    is_paid: marker.metaData.isPaid,
    price_reduction: isPriceReduction(marker.metaData.priceReducedTrackingLabel),
  };

  store.dispatch(segmentTrack({ eventName: 'Community Result Pin Displayed', customProperties }));
}

/**
 * Subscribes to the `markerUrlOpened$` observable and fires a segment event when triggered.
 */
markerUrlOpened$.pipe(
  filter(data => Boolean(data?.marker?.id)),
  filter(data => data?.marker?.type === MarkerType.HousingDevelopment.toString()),
).subscribe(triggerCommunityMapMarkerUrlOpenedEvent);

/**
 * Triggers the map marker clicked event for home markers.
 * 
 * @param {Object} marker - The marker object.
 */
function triggerPlanMapMarkerUrlOpenedEvent(data) {
  const { marker } = data;

  const customProperties = {
    id: marker.id,
    type: 'plan',
    price: marker.metaData.priceLow,
    is_paid: marker.metaData.isPaid,
    price_reduction: isPriceReduction(marker.metaData.priceReducedTrackingLabel),
  };

  store.dispatch(segmentTrack({ eventName: 'Home Result Pin Displayed', customProperties }));
}

/**
 * Subscribes to the `markerUrlOpened$` observable and fires a segment event when triggered.
 */
markerUrlOpened$.pipe(
  filter(data => Boolean(data?.marker?.id)),
  filter(data => data?.marker?.type === MarkerType.PlanUnit.toString()),
).subscribe(triggerPlanMapMarkerUrlOpenedEvent);

/**
 * Triggers the map marker clicked event for home markers.
 * 
 * @param {Object} marker - The marker object.
 */
function triggerSpecMapMarkerUrlOpenedEvent(data) {
  const { marker } = data;

  const customProperties = {
    id: marker.id,
    type: 'spec',
    price: marker.metaData.priceLow,
    is_paid: marker.metaData.isPaid,
    price_reduction: isPriceReduction(marker.metaData.priceReducedTrackingLabel),
  };

  store.dispatch(segmentTrack({ eventName: 'Home Result Pin Displayed', customProperties }));
}

/**
 * Subscribes to the `markerUrlOpened$` observable and fires a segment event when triggered.
 */
markerUrlOpened$
  .pipe(
    filter((data) => Boolean(data?.marker?.id)),
    filter((data) => data?.marker?.type === MarkerType.SpecUnit.toString()),
  )
  .subscribe(triggerSpecMapMarkerUrlOpenedEvent);

// #endregion

// #region
/**
 *  ! IMPRESSION EVENTS
 */
/**
 * getPaidCommunityListingCards: Gets the list of current paid community listing cards.
 * @returns A HTML Node list of current paid community cards.
 */
const getPaidCommunityListingCards = () => listViewSection.querySelectorAll(paidCommunityCardSelector);
/**
 * getPaidManufacturedCommunityListingCards: Gets the list of current paid community listing cards.
 * @returns A HTML Node list of current paid community cards.
 */
const getPaidManufacturedCommunityListingCards = () =>
  listViewSection.querySelectorAll(paidManufacturedCommunityCardSelector);

/**
* getBasicCustomCommunityListingCards: Gets the list of current basic community listing cards.
* @returns A HTML Node list of current basic community cards.
*/
const getPaidCustomCommunityListingCards = () =>
  listViewSection.querySelectorAll(`${paidCustomCommunityCardSelector}, ${paidBuildOnYourLotCommunityCardSelector}`);
/**
 * isAutoScroll: Gets the selector for the map marker to know if the Auto Scroll functionality is available.
 * @returns A bool to determine if it is auto scroll or not.
 */
const isAutoScroll = () => {
  const state = store.getState();

  const selectedMarkerInformation = selectSelectedMarker(state);

  if (!selectedMarkerInformation) {
    return false;
  }

  const {marker, resultType } = selectedMarkerInformation;

  return marker && resultType === ResultType.Communities.toString();
};

/**
 * triggerPaidCommunityResultsDisplayedEvent: Triggers the Community Results Displayed impression event.
 * @param {HTMLElement} viewedCard Shown card.
 */
const triggerPaidCommunityResultsDisplayedEvent = (viewedCard) => {
  const cardDataset = viewedCard.dataset;
  const position = Number(cardDataset.index) + 1;
  const customProperties = {
    market_id: cardDataset.marketId,
    market_name: `${cardDataset.marketName}, ${cardDataset.stateAbbreviation}`,
    city: cardDataset.city,
    state: cardDataset.stateAbbreviation,
    zip: cardDataset.zip,
    builder_id: cardDataset.builderId,
    builder_name: cardDataset.brandName,
    community_id: cardDataset.id,
    community_name: cardDataset.communityName,
    community_status: cardDataset.marketingStatusLabel,
    primary_image_url: cardDataset.imageUrl,
    community_low_price: cardDataset.priceLow,
    community_high_price: cardDataset.priceHigh,
    position,
    image_1_url: cardDataset.imageUrl,
    image_2_url: cardDataset.secondaryImageUrl,
    image_3_url: cardDataset.tertiaryImageUrl,
    is_zero_results: isNotResultPage(),
    is_auto_scroll: isAutoScroll(),
    is_paid: true,
    is_spotlight: !!cardDataset.wasMatchedAsSpotlightResult,
    community_type: cardDataset.communityType,
    is_neighborhood: isNeighborhoodPage(),
    is_schooldistrict: isSchoolDistrictPage(),
  };
  store.dispatch(segmentImpression({ eventName: 'Community Result Displayed', customProperties }));
};
const paidCommunityResultsDisplayedEventObserver = new ImpressionEventObserver(
  triggerPaidCommunityResultsDisplayedEvent
);

/**
 * getBasicCommunityListingCards: Gets the list of current basic community listing cards.
 * @returns A HTML Node list of current basic community cards.
 */
const getBasicCommunityListingCards = () => listViewSection.querySelectorAll(basicCommunityCardSelector);
/**
 * getBasicManufacturedCommunityListingCards: Gets the list of current basic community listing cards.
 * @returns A HTML Node list of current basic community cards.
 */
const getBasicManufacturedCommunityListingCards = () =>
  listViewSection.querySelectorAll(basicManufacturedCommunityCardSelector);
/**
* getBasicCustomCommunityListingCards: Gets the list of current basic community listing cards.
* @returns A HTML Node list of current basic community cards.
*/
const getBasicCustomCommunityListingCards = () =>
  listViewSection.querySelectorAll(`${basicCustomCommunityCardSelector}, ${basicBuildOnYourLotCommunityCardSelector}`);
/**
 * triggerBasicCommunityResultsDisplayedEvent: Triggers the Community Results Displayed impression event.
 * @param {HTMLElement} viewedCard Shown card.
 */
const triggerBasicCommunityResultsDisplayedEvent = (viewedCard) => {
  const cardDataset = viewedCard.dataset;
  const position = Number(cardDataset.index) + 1;
  const customProperties = {
    market_id: cardDataset.marketId,
    market_name: `${cardDataset.marketName}, ${cardDataset.stateAbbreviation}`,
    city: cardDataset.city,
    state: cardDataset.stateAbbreviation,
    zip: cardDataset.zip,
    builder_id: cardDataset.builderId,
    builder_name: cardDataset.brandName,
    community_id: cardDataset.id,
    community_name: cardDataset.communityName,
    community_status: cardDataset.marketingStatusLabel,
    primary_image_url: cardDataset.imageUrl,
    community_low_price: cardDataset.priceLow,
    community_high_price: cardDataset.priceHigh,
    position,
    is_paid: false,
    image_1_url: cardDataset.imageUrl,
    image_2_url: cardDataset.secondaryImageUrl,
    image_3_url: cardDataset.tertiaryImageUrl,
    is_spotlight: !!cardDataset.wasMatchedAsSpotlightResult,
    is_zero_results: isNotResultPage(),
    is_auto_scroll: isAutoScroll(),
    is_neighborhood: isNeighborhoodPage(),
    is_schooldistrict: isSchoolDistrictPage(),
  };
  store.dispatch(segmentImpression({ eventName: 'Community Result Displayed', customProperties }));
};

const basicCommunityResultsDisplayedEventObserver = new ImpressionEventObserver(
  triggerBasicCommunityResultsDisplayedEvent
);

/**
 * bindBasicCommunityResultsDisplayedEvents: Observe each card to check when some card is viewed.
 */
const bindBasicCommunityResultsDisplayedEvents = () => {
  const basicCommunityListingCards = getBasicCommunityListingCards();
  const basicManufacturedCommunityListingCards = getBasicManufacturedCommunityListingCards();
  const basicCustomCommunityListingCards = getBasicCustomCommunityListingCards();
  const basicCommunitiesListings = [
    ...basicCommunityListingCards,
    ...basicManufacturedCommunityListingCards,
    ...basicCustomCommunityListingCards
  ];
  const basicCommunityContainerObserver = basicCommunityResultsDisplayedEventObserver.getIntersectionObserver();
  basicCommunitiesListings.forEach((card) => basicCommunityContainerObserver.observe(card));
};

/**
 * getBasicSpecListingCards: Gets the list of current basic spec cards.
 * @returns A HTML Node list of current basic spec cards.
 */
const getBasicSpecListingCards = () => listViewSection.querySelectorAll(basicSpecCardSelector);
/**
 * getBasicManufacturedSpecListingCards: Gets the list of current basic manufactured spec cards.
 * @returns A HTML Node list of current basic manufactured spec cards.
 */
const getBasicManufacturedSpecListingCards = () => listViewSection.querySelectorAll(basicManufacturedSpecCardSelector);
/**
 * getBasicCustomSpecListingCards: Gets the list of current basic custom spec cards.
 * @returns A HTML Node list of current basic custom spec cards.
 */
const getBasicCustomSpecListingCards = () => listViewSection.querySelectorAll(`${basicCustomSpecCardSelector}, ${basicBuildOnYourLotSpecCardSelector}`);
/**
 * triggerBasicSpecResultsDisplayedEvent: Triggers the Basic Spec Results Displayed impression event.
 * @param {HTMLElement} viewedCard Shown card.
 */
const triggerBasicSpecResultsDisplayedEvent = (viewedCard) => {
  const cardDataset = viewedCard.dataset;
  const position = Number(cardDataset.index) + 1;
  const customProperties = {
    market_id: cardDataset.marketId,
    market_name: `${cardDataset.marketName}, ${cardDataset.stateAbbreviation}`,
    city: cardDataset.city,
    state: cardDataset.stateAbbreviation,
    zip: cardDataset.zip,
    builder_id: cardDataset.builderId,
    builder_name: cardDataset.brandName,
    community_id: cardDataset.communityId,
    spec_id: cardDataset.specId,
    spec_name: cardDataset.name,
    plan_id: cardDataset.planId,
    community_name: cardDataset.communityName,
    primary_image_url: cardDataset.imageUrl,
    position,
    is_paid: false,
    is_spotlight: !!cardDataset.wasMatchedAsSpotlightResult,
    community_type: cardDataset.communityType,
    image_1_url: cardDataset.imageUrl,
    is_zero_results: isNotResultPage(),
    is_neighborhood: isNeighborhoodPage(),
    is_schooldistrict: isSchoolDistrictPage(),
  }
  store.dispatch(segmentImpression({ eventName: 'Home Result Displayed', customProperties }));
};
const basicSpecListingResultsDisplayedEventObserver = new ImpressionEventObserver(
  triggerBasicSpecResultsDisplayedEvent
);
/**
 * bindBasicSpecResultsDisplayedEvents: Observe each card to check when some card is viewed.
 */
const bindBasicSpecResultsDisplayedEvents = () => {
  const basicSpecListingCards = getBasicSpecListingCards();
  const basicManufacturedSpecListingCards = getBasicManufacturedSpecListingCards();
  const basicCustomSpecListingCards = getBasicCustomSpecListingCards();
  const basicHomesListingCards = [
    ...basicSpecListingCards,
    ...basicManufacturedSpecListingCards,
    ...basicCustomSpecListingCards];
  const basicSpecListingContainerObserver = basicSpecListingResultsDisplayedEventObserver.getIntersectionObserver();
  basicHomesListingCards.forEach((card) => basicSpecListingContainerObserver.observe(card));
};
/**
 * getBasicPlanListingCards: Gets the list of current basic plan cards.
 * @returns A HTML Node list of current basic plan cards.
 */
const getBasicPlanListingCards = () => listViewSection.querySelectorAll(basicPlanCardSelector);
/**
 * getBasicManufacturedPlanListingCards: Gets the list of current basic manufactured plan cards.
 * @returns A HTML Node list of current basic manufactured plan cards.
 */
const getBasicManufacturedPlanListingCards = () => listViewSection.querySelectorAll(basicManufacturedPlanCardSelector);

/**
 * getBasicCustomPlanListingCards: Gets the list of current basic custom plan cards.
 * @returns A HTML Node list of current basic custom plan cards.
 */
const getBasicCustomPlanListingCards = () => listViewSection.querySelectorAll(`${basicCustomPlanCardSelector}, ${basicBuildOnYourLotPlanCardSelector}`);

/**
 * triggerBasicPlanResultsDisplayedEvent: Triggers the Basic Plan Results Displayed impression event.
 * @param {HTMLElement} viewedCard Shown card.
 */
const triggerBasicPlanResultsDisplayedEvent = (viewedCard) => {
  const cardDataset = viewedCard.dataset;
  const position = Number(cardDataset.index) + 1;
  const customProperties = {
    market_id: cardDataset.marketId,
    market_name: `${cardDataset.marketName}, ${cardDataset.stateAbbreviation}`,
    city: cardDataset.city,
    state: cardDataset.stateAbbreviation,
    zip: cardDataset.zip,
    builder_id: cardDataset.builderId,
    builder_name: cardDataset.brandName,
    community_id: cardDataset.communityId,
    plan_id: cardDataset.planId,
    plan_name: cardDataset.name,
    community_name: cardDataset.communityName,
    primary_image_url: cardDataset.imageUrl,
    position,
    is_paid: false,
    is_spotlight: !!cardDataset.wasMatchedAsSpotlightResult,
    community_type: cardDataset.communityType,
    image_1_url: cardDataset.imageUrl,
    is_zero_results: isNotResultPage(),
    is_neighborhood: isNeighborhoodPage(),
    is_schooldistrict: isSchoolDistrictPage(),
  }
  store.dispatch(segmentImpression({ eventName: 'Home Result Displayed', customProperties }));
};
const basicPlanListingResultsDisplayedEventObserver = new ImpressionEventObserver(
  triggerBasicPlanResultsDisplayedEvent
);
/**
 * bindBasicPlanResultsDisplayedEvents: Observe each card to check when some card is viewed.
 */
const bindBasicPlanResultsDisplayedEvents = () => {
  const basicPlanListingCards = getBasicPlanListingCards();
  const basicManufacturedPlanListingCards = getBasicManufacturedPlanListingCards();
  const basicCustomPlanListingCards = getBasicCustomPlanListingCards();
  const basicPlanHomesListingCards = [
    ...basicPlanListingCards,
    ...basicManufacturedPlanListingCards,
    ...basicCustomPlanListingCards];
  const basicPlanListingContainerObserver = basicPlanListingResultsDisplayedEventObserver.getIntersectionObserver();
  basicPlanHomesListingCards.forEach((card) => basicPlanListingContainerObserver.observe(card));
};
/**
 * getBasicListingCards: Gets the list of current basic listing cards.
 * @returns A HTML Node list of current basic listing cards.
 */
const getBasicListingCards = () => listViewSection.querySelectorAll(basicListingCardSelector);
/**
 * triggerBasicListingResultsDisplayedEvent: Triggers the Basic Listing Results Displayed impression event.
 * @param {HTMLElement} viewedCard Shown card.
 */
const triggerBasicListingResultsDisplayedEvent = (viewedCard) => {
  const cardDataset = viewedCard.dataset;
  const position = Number(cardDataset.index) + 1;
  const customProperties = {
    market_id: cardDataset.marketId,
    market_name: `${cardDataset.marketName}, ${cardDataset.stateAbbreviation}`,
    city: cardDataset.city,
    state: cardDataset.stateAbbreviation,
    zip: cardDataset.zip,
    community_id: cardDataset.communityId,
    id: cardDataset.id,
    community_name: cardDataset.communityName,
    primary_image_url: cardDataset.imageUrl,
    position,
    is_paid: false,
    is_zero_results: isNotResultPage(),
  };
  store.dispatch(segmentImpression({ eventName: 'Basic Detail Result Displayed', customProperties }));
};
const basicListingResultsDisplayedEventObserver = new ImpressionEventObserver(
  triggerBasicListingResultsDisplayedEvent
);
/**
 * bindBasicListingResultsDisplayedEvents: Observe each card to check when some card is viewed.
 */
const bindBasicListingResultsDisplayedEvents = () => {
  const basicListingCards = getBasicListingCards();
  const basicListingContainerObserver = basicListingResultsDisplayedEventObserver.getIntersectionObserver();
  basicListingCards.forEach((card) => basicListingContainerObserver.observe(card));
};

const isNewResultsInformationLoaded = ({
  fetchResultListingsStatus,
  fetchResultCountsStatus,
  housingProductLocationFetchStatus,
}) =>
  fetchResultListingsStatus !== LoadingStatus.Loading &&
  fetchResultCountsStatus !== LoadingStatus.Loading &&
  housingProductLocationFetchStatus !== LoadingStatus.Loading;

/**
 * bindPaidCommunityResultsDisplayedEvents: Observe each card to check when some card is viewed.
 */
const bindPaidCommunityResultsDisplayedEvents = () => {
  const communityListingCards = getPaidCommunityListingCards();
  const manufacturedCommunityListingCards = getPaidManufacturedCommunityListingCards();
  const customCommunityListingCards = getPaidCustomCommunityListingCards();
  const paidCommunityListings = [
    ...communityListingCards,
    ...manufacturedCommunityListingCards,
    ...customCommunityListingCards,
  ];
  const communityContainerObserver = paidCommunityResultsDisplayedEventObserver.getIntersectionObserver();
  paidCommunityListings.forEach((card) => communityContainerObserver.observe(card));
};
const resultsChanged$ = store$.pipe(
  map(selectRequestStatus),
  distinctUntilChanged(),
  filter(isNewResultsInformationLoaded)
);

/**
 * triggerCommunityStaticMapHoverEvent will dispatch the event of hovering in a community
 * static map. It will track properties as the identifier of the static map, and how many
 * time lasted the hovering gesture.
 */
const triggerCommunityStaticMapHoverEvent = ({ communityStaticMap, hoverDuration, deviceType }) => {
  const customProperties = {
    community_id: communityStaticMap.id,
    community_name: communityStaticMap.name,
    hover_duration: hoverDuration,
    device_type: deviceType,
  };

  setTimeout(()=> store.dispatch(segmentTrack({ eventName: 'map_hover', customProperties })), 0);
}

communityStaticMapHovered$.subscribe(triggerCommunityStaticMapHoverEvent);

/**
 * triggerCommunityStaticMapClickEvent will dispatch the event of clicking in a community.
 * static map.
 */
const triggerCommunityStaticMapClickEvent = ({ communityStaticMap, deviceType }) => {
  const customProperties = {
    community_id: communityStaticMap.id,
    community_name: communityStaticMap.name,
    device_type: deviceType,
  };

  setTimeout(()=> store.dispatch(segmentTrack({ eventName: 'map_click', customProperties })), 0);
}

communityStaticMapClicked$.subscribe(triggerCommunityStaticMapClickEvent);

/**
 * triggerCommunityStaticMapDoubleClickEvent will dispatch the event of double clicking in a community.
 */
const triggerCommunityStaticMapDoubleClickEvent = ({ communityStaticMap, deviceType }) => {
  const customProperties = {
    community_id: communityStaticMap.id,
    community_name: communityStaticMap.name,
    device_type: deviceType,
    redirected_to: communityStaticMap.url,
  };

  setTimeout(()=> store.dispatch(segmentTrack({ eventName: 'map_double_click', customProperties })), 0);
}

communityStaticMapDoubleClicked$.subscribe(triggerCommunityStaticMapDoubleClickEvent);

/**
 * triggerCommunityStaticMapZoomChangedEvent will dispatch an event with information like
 * identifier and name of the community static map that received the zoom change. Also, it
 * will track if the zoom change was a zoom in or out.
 */
const triggerCommunityStaticMapZoomChangedEvent = ({ communityStaticMap, zoomGesture, deviceType, zoomLevel }) => {
  const customProperties = {
    community_id: communityStaticMap.id,
    community_name: communityStaticMap.name,
    device_type: deviceType,
    zoom_level: zoomLevel,
  };

  const eventName = zoomGesture === "zoomIn" ? "map_zoom_in" : "map_zoom_out";

  setTimeout(()=> store.dispatch(segmentTrack({ eventName, customProperties })), 0);
}

communityStaticMapZoomChanged$.subscribe(triggerCommunityStaticMapZoomChangedEvent);

resultsChanged$.subscribe(bindPaidCommunityResultsDisplayedEvents);
resultsChanged$.subscribe(bindBasicCommunityResultsDisplayedEvents);
resultsChanged$.subscribe(bindBasicPlanResultsDisplayedEvents);
resultsChanged$.subscribe(bindBasicSpecResultsDisplayedEvents);
resultsChanged$.subscribe(bindBasicListingResultsDisplayedEvents);

bindPaidCommunityResultsDisplayedEvents();
bindBasicCommunityResultsDisplayedEvents();
bindBasicListingResultsDisplayedEvents();
bindBasicPlanResultsDisplayedEvents();
bindBasicSpecResultsDisplayedEvents();

/**
 * getPaidPlanListingCards: Gets the list of current paid plan listing cards.
 * @returns A HTML Node list of current paid plan cards.
 */
const getPaidPlanListingCards = () => listViewSection.querySelectorAll(paidPlanCardSelector);
/**
 * getPaidSpecListingCards: Gets the list of current spec community listing cards.
 * @returns A HTML Node list of current paid spec cards.
 */
const getPaidSpecListingCards = () => listViewSection.querySelectorAll(paidSpecCardSelector);
/**
 * getPaidManufacturedListingCards: Gets the list of current paid manufactured listing cards.
 * @returns A HTML Node list of current paid manufactured home cards.
 */
const getPaidManufacturedListingCards = () => listViewSection.querySelectorAll(`${paidManufacturedPlanCardSelector},
${paidManufacturedSpecCardSelector}`);

/**
 * getPaidCustomListingCards: Gets the list of current paid custom listing cards.
 * @returns A HTML Node list of current paid manufactured home cards.
 */
const getPaidCustomListingCards = () => listViewSection.querySelectorAll(`${paidCustomPlanCardSelector}, 
${paidCustomSpecCardSelector}, ${paidBuildOnYourLotPlanCardSelector}, ${paidBuildOnYourLotSpecCardSelector}`);
/**
 * triggerPaidHomeResultsDisplayedEvent: Triggers the Home Results Displayed impression event.
 * @param {HTMLElement} viewedCard Shown card.
 */
const triggerPaidHomeResultsDisplayedEvent = (viewedCard) => {
  const cardDataset = viewedCard.dataset;
  const position = Number(cardDataset.index) + 1;

  const homeType = cardDataset.type;
  if (homeType === 'plan') {
    const customProperties = {
      market_id: cardDataset.marketId,
      market_name: `${cardDataset.marketName}, ${cardDataset.stateAbbreviation}`,
      city: cardDataset.city,
      state: cardDataset.stateAbbreviation,
      zip: cardDataset.zip,
      builder_id: cardDataset.builderId,
      builder_name: cardDataset.brandName,
      community_id: cardDataset.communityId,
      plan_id: cardDataset.planId,
      plan_name: cardDataset.name,
      community_name: cardDataset.communityName,
      community_status: cardDataset.marketingStatusLabel,
      primary_image_url: cardDataset.imageUrl,
      community_low_price: cardDataset.priceLow,
      community_high_price: cardDataset.priceHigh,
      position,
      image_1_url: cardDataset.imageUrl,
      image_2_url: cardDataset.secondaryImageUrl,
      image_3_url: cardDataset.tertiaryImageUrl,
      is_zero_results: isNotResultPage(),
      is_paid: true,
      is_spotlight: !!cardDataset.wasMatchedAsSpotlightResult,
      community_type: cardDataset.communityType,
      is_neighborhood: isNeighborhoodPage(),
      is_schooldistrict: isSchoolDistrictPage(),
    }
    store.dispatch(segmentImpression({ eventName: 'Home Result Displayed', customProperties }));
  }

  if (homeType === 'spec') {
    const customProperties = {
      market_id: cardDataset.marketId,
      market_name: `${cardDataset.marketName}, ${cardDataset.stateAbbreviation}`,
      city: cardDataset.city,
      state: cardDataset.stateAbbreviation,
      zip: cardDataset.zip,
      builder_id: cardDataset.builderId,
      builder_name: cardDataset.brandName,
      community_id: cardDataset.communityId,
      spec_id: cardDataset.specId,
      spec_name: cardDataset.name,
      plan_id: cardDataset.planId,
      community_name: cardDataset.communityName,
      community_status: cardDataset.marketingStatusLabel,
      primary_image_url: cardDataset.imageUrl,
      community_low_price: cardDataset.priceLow,
      community_high_price: cardDataset.priceHigh,
      position,
      image_1_url: cardDataset.imageUrl,
      image_2_url: cardDataset.secondaryImageUrl,
      image_3_url: cardDataset.tertiaryImageUrl,
      is_zero_results: isNotResultPage(),
      is_paid: true,
      is_spotlight: !!cardDataset.wasMatchedAsSpotlightResult,
      community_type: cardDataset.communityType,
      is_neighborhood: isNeighborhoodPage(),
      is_schooldistrict: isSchoolDistrictPage(),
    }

    store.dispatch(segmentImpression({ eventName: 'Home Result Displayed', customProperties }));
  }
};
const paidHomeResultsDisplayedEventObserver = new ImpressionEventObserver(triggerPaidHomeResultsDisplayedEvent);

/**
 * bindPaidHomeResultsDisplayedEvents: Observe each home card to check when some card is viewed.
 */
const bindPaidHomeResultsDisplayedEvents = () => {
  const paidPlanListingCards = getPaidPlanListingCards();
  const paidSpecListingCards = getPaidSpecListingCards();
  const paidManufacturedListingCards = getPaidManufacturedListingCards();
  const paidCustomListingCards = getPaidCustomListingCards();
  const paidHomeListingCards = [
    ...paidPlanListingCards,
    ...paidSpecListingCards,
    ...paidManufacturedListingCards,
    ...paidCustomListingCards,
  ];
  const homeContainerObserver = paidHomeResultsDisplayedEventObserver.getIntersectionObserver();
  paidHomeListingCards.forEach((card) => homeContainerObserver.observe(card));
};

resultsChanged$.subscribe(bindPaidHomeResultsDisplayedEvents);
bindPaidHomeResultsDisplayedEvents();

// #endregion

/**
 * Triggers the carousel closed event for plan markers.
 * 
 * @param {Object} marker - The marker object.
 */
function triggerPlanCarouselClosedEvent(data) {
  const marker = data.activeItem;

  const customProperties = {
    market_id: marker.MarketId,
    plan_id: marker.Id,
    community_id: marker.CommunityId,
    plan_name: marker.Name,
    price: marker.Price,
    city: marker.City,
    state: marker.StateAbbreviation,
    zip: marker.Zip,
    is_paid: !marker.IsBasic,
    current_card_position: data.currentCardPosition,
    next_card_position: data.nextCardPosition,
    listing_id: data.listingId,
    direction: data.direction,
    builder_id: marker.BuilderId,
    builder_name: marker.BrandName,
    community_status: marker.CommunityStatus.Name, 
    primary_image_url: marker.Thumbnail,
    community_low_price: marker.PriceLow,
    community_high_price: marker.PriceHigh,
  };

  setTimeout(() => {
    store.dispatch(segmentTrack({ eventName: 'carousel_closed', customProperties }));
  });
}

/**
 * Triggers the carousel closed event for spec markers.
 * 
 * @param {Object} marker - The marker object.
 */
function triggerSpecCarouselClosedEvent(data) {
  const marker = data.activeItem;

  const customProperties = {
    market_id: marker.MarketId,
    spec_id: marker.Id,
    community_id: marker.CommunityId,
    spec_name: marker.Name,
    price: marker.Price,
    city: marker.City,
    state: marker.StateAbbreviation,
    zip: marker.Zip,
    is_paid: !marker.IsBasic,
    current_card_position: data.currentCardPosition,
    next_card_position: data.nextCardPosition,
    listing_id: data.listingId,
    direction: data.direction,
    builder_id: marker.BuilderId,
    builder_name: marker.BrandName,
    community_status: marker.CommunityStatus.Name, 
    primary_image_url: marker.Thumbnail,
    community_low_price: marker.PriceLow,
    community_high_price: marker.PriceHigh,
  };

  setTimeout(() => {
    store.dispatch(segmentTrack({ eventName: 'carousel_closed', customProperties }));
  });
}

/**
 * Triggers the carousel scrolled event for plan markers.
 * 
 * @param {Object} marker - The marker object.
 */
function triggerCommunityCarouselClosedEvent(data) {
  const marker = data.activeItem;

  const customProperties = {
    market_id: marker.MarketId,
    community_id: marker.Id,
    community_name: marker.Name,
    price: marker.Price,
    city: marker.City,
    state: marker.StateAbbreviation,
    zip: marker.Zip,
    is_paid: !marker.IsBasic,
    current_card_position: data.currentCardPosition,
    next_card_position: data.nextCardPosition,
    listing_id: data.listingId,
    direction: data.direction,
    builder_id: marker.BuilderId,
    builder_name: marker.BrandName,
    community_status: marker.MarketingStatus.Name, 
    primary_image_url: marker.Thumbnail,
    community_low_price: marker.PriceLow,
    community_high_price: marker.PriceHigh,
  };

  setTimeout(() => {
    store.dispatch(segmentTrack({ eventName: 'carousel_closed', customProperties }));
  });
}

/**
 * Triggers the carousel scrolled event for plan markers.
 * @param {Object} data - The data object containing carousel scroll information.
 */
function triggerPlanCarouselScrolledEvent(data) {
  const marker = data.activeItem;

  const customProperties = {
    current_card_position: data.currentCardPosition,
    next_card_position: data.nextCardPosition,
    listing_id: data.listingId,
    direction: data.direction,
    market_id: marker.MarketId,
    plan_id: marker.Id,
    community_id: marker.CommunityId,
    plan_name: marker.Name,
    price: marker.Price,
    city: marker.City,
    state: marker.StateAbbreviation,
    zip: marker.Zip,
    is_paid: !marker.IsBasic,
    builder_id: marker.BuilderId,
    builder_name: marker.BrandName,
    community_status: marker.CommunityStatus.Name, 
    primary_image_url: marker.Thumbnail,
    community_low_price: marker.PriceLow,
    community_high_price: marker.PriceHigh,
  };

  setTimeout(() => {
    store.dispatch(segmentTrack({ eventName: 'carousel_card_swiped', customProperties }));
  });
}

/**
 * Triggers the carousel scrolled event for spec markers.
 * @param {Object} spec - The marker object.
 */
function triggerSpecCarouselScrolledEvent(data) {
  const marker = data.activeItem;

  const customProperties = {
    market_id: marker.MarketId,
    spec_id: marker.Id,
    community_id: marker.CommunityId,
    spec_name: marker.Name,
    price: marker.Price,
    city: marker.City,
    state: marker.StateAbbreviation,
    zip: marker.Zip,
    is_paid: !marker.IsBasic,
    current_card_position: data.currentCardPosition,
    next_card_position: data.nextCardPosition,
    listing_id: data.listingId,
    direction: data.direction,
    builder_id: marker.BuilderId,
    builder_name: marker.BrandName,
    community_status: marker.CommunityStatus.Name, 
    primary_image_url: marker.Thumbnail,
    community_low_price: marker.PriceLow,
    community_high_price: marker.PriceHigh,
  };

  setTimeout(() => {
    store.dispatch(segmentTrack({ eventName: 'carousel_card_swiped', customProperties }));
  });
}

/**
 * Triggers the carousel scrolled event for community markers.
 * 
 * @param {Object} marker - The marker object.
 */
function triggerCommunityCarouselScrolledEvent(data) {
  const marker = data.activeItem;

  const customProperties = {
    market_id: marker.MarketId,
    brand_name: marker.BrandName,
    community_id: marker.Id,
    community_name: marker.Name,
    price: marker.Price,
    city: marker.City,
    state: marker.StateAbbreviation,
    zip: marker.Zip,
    is_paid: !marker.IsBasic,
    current_card_position: data.currentCardPosition,
    next_card_position: data.nextCardPosition,
    listing_id: data.listingId,
    direction: data.direction,
    builder_id: marker.BuilderId,
    builder_name: marker.BrandName,
    community_status: marker.MarketingStatus.Name, 
    primary_image_url: marker.Thumbnail,
    community_low_price: marker.PriceLow,
    community_high_price: marker.PriceHigh,
  };

  setTimeout(() => {
    store.dispatch(segmentTrack({ eventName: 'carousel_card_swiped', customProperties }));
  });
}

/**
 * Triggers the saved search clicked event.
 */
const triggerSavedSearchClickedEvent = () => {
  const state = store.getState();

  const customProperties = {
    is_zero_results: isNotResultPage(),
    current_page_number: selectPage(state),
    filters_applied: selectAppliedFacets(state)
  };

  setTimeout(() => {
    store.dispatch(segmentTrack({ eventName: 'Save Search Clicked', customProperties }));
  });
}

/**
 * Triggers the saved search modal shown event.
 */
const triggerSavedSearchModalShown = (modal) => {
  const state = store.getState();
  
  const customProperties = {
    is_zero_results: isNotResultPage(),
    current_page_number: selectPage(state),
    filters_applied: selectAppliedFacets(state),
    search_name: modal.variation,
  };

  setTimeout(() => {
    store.dispatch(segmentTrack({ eventName: 'Save Search Modal Viewed', customProperties }));
  });
}

/**
 * Triggers the keep searching button clicked event.
 */
const triggerSavedSearchKeepSearchingEvent = (target) => {
  const state = store.getState();

  const customProperties = {
    is_zero_results: isNotResultPage(),
    current_page_number: selectPage(state),
    filters_applied: selectAppliedFacets(state),
    search_name: target.getAttribute('data-saved-search-name'),
  };

  setTimeout(() => {
    store.dispatch(segmentTrack({ eventName: 'Keep Searching Clicked', customProperties }));
  });
}

/**
 * Triggers the saved search dashboard link clicked event.
 */
const triggerSavedSearchDashboardEvent = (target) => {
  const state = store.getState();

  const customProperties = {
    is_zero_results: isNotResultPage(),
    current_page_number: selectPage(state),
    filters_applied: selectAppliedFacets(state),
    search_name: target.getAttribute('data-saved-search-name'),
  };

  setTimeout(() => {
    store.dispatch(segmentTrack({ eventName: 'Saved Search Link Clicked', customProperties }));
  });
}

carouselCloseButtonClicked$.pipe(
  filter(marker => Boolean(marker)),
  filter(marker => marker.type === MarkerType.PlanUnit.toString()),
).subscribe(triggerPlanCarouselClosedEvent);

carouselCloseButtonClicked$.pipe(
  filter(marker => Boolean(marker)),
  filter(marker => marker.type === MarkerType.SpecUnit.toString()),
).subscribe(triggerSpecCarouselClosedEvent);

carouselCloseButtonClicked$.pipe(
  filter(marker => Boolean(marker)),
  filter(marker => marker.type === MarkerType.HousingDevelopment.toString()),
).subscribe(triggerCommunityCarouselClosedEvent);

summaryCardsCarouselScrolled$.pipe(
  filter(marker => Boolean(marker)),
  filter(marker => marker.type === MarkerType.PlanUnit.toString()),
).subscribe(triggerPlanCarouselScrolledEvent);

summaryCardsCarouselScrolled$.pipe(
  filter(marker => Boolean(marker)),
  filter(marker => marker.type === MarkerType.SpecUnit.toString()),
).subscribe(triggerSpecCarouselScrolledEvent);

summaryCardsCarouselScrolled$.pipe(
  filter(marker => Boolean(marker)),
  filter(marker => marker.type === MarkerType.HousingDevelopment.toString()),
).subscribe(triggerCommunityCarouselScrolledEvent); 

/**
 * Triggers the saved search clicked event when the user clicks on a saved search button.
 */
onSavedSearchClicked$.subscribe(triggerSavedSearchClickedEvent);

/*
  Triggers the saved search modal shown.
*/
createdSaveSearchModalShown$.subscribe(triggerSavedSearchModalShown);

/**
 * Triggers the saved search clicked event when the saved search button is clicked.
 */
onKeepSearchingButtonClicked$.subscribe(triggerSavedSearchKeepSearchingEvent);

/**
 * Triggers the saved search clicked event when the saved search button is clicked.
 */
onDashboardLinkClicked$.subscribe(triggerSavedSearchDashboardEvent);